import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Network,
  Plus,
  Box,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Grid3x3,
  Trash2,
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  UserCheck,
  X,
  Loader2,
  Check,
  ListChecks,
  Download,
  Edit2,
  XCircle,
  Building2,
  Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import amsEquipmentService from '../../../services/amsEquipmentService';
import { assetService } from '../../../services/assetService';
import { Asset } from '../../../types/asset';
import amsProjectAssetService from '../../../services/amsProjectAssetService';
import { ProjectAsset } from '../../../services/projectAssetService';
import manufacturerService from '../../../services/manufacturerService';
import makeAndModelService from '../../../services/makeAndModelService';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { canonicalEquipmentKind } from '../utils/equipmentKind';
import { getRecommendedChildren, StandardChildConfig } from '../../project/components/solarStandardConfigs';
import BulkEquipmentDrawer from '../../project/components/BulkEquipmentDrawer';
import EquipmentAssignmentDrawer from '../../project/components/EquipmentAssignmentDrawer';
import ProjectAssetCheckpointDrawer from '../../project/components/ProjectAssetCheckpointDrawer';
import EquipmentEditDrawer from '../../project/components/EquipmentEditDrawer';
import ConfirmationDrawer from '../../../components/common/ConfirmationDrawer';
import ManufacturerForm from '../../manufacturer/components/ManufacturerForm';
import MakeAndModelForm from '../../manufacturer/components/MakeAndModelForm';

import type { ProjectAssetTreeViewProps, VisualEquipment } from '../../project/components/project-asset-tree-view/types';
import { DESIGN_TOKENS, LAYOUT, TREE_GRID, EQUIPMENT_CARD_BG, EQUIPMENT_CARD_BORDER } from '../../project/components/project-asset-tree-view/constants';
import { getAssetTypeConfig } from '../../project/components/project-asset-tree-view/assetTypeConfig';
import { calculateTreeLayout, calculateGridDimensions } from '../../project/components/project-asset-tree-view/treeLayout';
import TreeConnections from '../../project/components/project-asset-tree-view/components/TreeConnections';
import TreeGroupBorders from '../../project/components/project-asset-tree-view/components/TreeGroupBorders';
import { ZoomControls } from '../../project/components/project-asset-tree-view/components/ZoomControls';
import { TreeLoadingState, TreeErrorState } from '../../project/components/project-asset-tree-view/components/TreeLoadingStates';
import { useTreeZoom } from '../../project/components/project-asset-tree-view/hooks/useTreeZoom';
import { useTreeNavigation } from '../../project/components/project-asset-tree-view/hooks/useTreeNavigation';
import { useEquipmentQuery, useAssigneesQuery } from './hooks/useEquipmentQuery';
import amsAuditCheckpointService from '../../../services/amsAuditCheckpointService';
import { amsCheckpointService } from '../../../services/amsCheckpointService';
import auditTypeService from '../../../services/auditTypeService';
import AmsAuditCheckpointForm from './AmsAuditCheckpointForm';
import type { AmsAuditApproval } from '../../../services/amsAuditApprovalService';

type AmsProjectAssetTreeViewProps = ProjectAssetTreeViewProps & {
  selectedAuditApproval?: AmsAuditApproval | null;
  projectApprovalSections?: {
    projectOverview?: { status?: string };
    assetsAndEquipment?: { status?: string };
  };
  /** Called when user updates a rejected checkpoint so parent can mark audit as Updated */
  onAuditCheckpointsUpdated?: (auditId: string) => void;
};

export default function AmsProjectAssetTreeView({
  project,
  onEquipmentUpdate,
  onOpenCheckpoints,
  onAddMasterAsset,
  availableAssets,
  projectAssets,
  selectedAuditId,
  selectedAudit,
  onCheckpointsUpdated,
  selectedAuditApproval = null,
  projectApprovalSections,
  onAuditCheckpointsUpdated
}: AmsProjectAssetTreeViewProps) {
  // React Query for equipment data with caching and retry logic
  const projectId = project?._id || project?.id;
  // Assets/equipment are audit-scoped — all CRUD in this tree targets the selected audit.
  const auditId = selectedAuditId || null;
  const {
    data: equipmentData,
    isLoading: equipmentLoading,
    isError: equipmentError,
    error: equipmentErrorDetails,
    refetch: refetchEquipment
  } = useEquipmentQuery({ projectId: projectId || '', amsProjectAuditId: auditId });

  // React Query for assignees data
  const {
    data: assigneesData,
    isLoading: assigneesLoading,
    refetch: fetchAssignees
  } = useAssigneesQuery(projectId || '');

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Extract equipment list from React Query data
  const equipmentList = equipmentData?.data || [];
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [addModalState, setAddModalState] = useState<{
    open: boolean;
    ghost?: any;
    quantity: number;
    namingPattern?: string;
    manufacturer?: string;
    makeAndModel?: string;
    makeAndModelId?: string;
    stringCount?: number;
    warranty?: {
      provider: string;
      termsYears: number;
      terms: string;
    };
    vendor?: {
      name: string;
    };
    notes?: string;
    documents: Array<{
      documentName: string;
      documentType: string;
      documentFile: string;
    }>;
  }>({
    open: false,
    quantity: 1,
    manufacturer: '',
    makeAndModel: '',
    makeAndModelId: '',
    warranty: { provider: '', termsYears: 0, terms: '' },
    vendor: { name: '' },
    notes: '',
    documents: []
  });

  // State for new document input in bulk creation
  const [newBulkDocument, setNewBulkDocument] = useState({
    documentName: '',
    documentType: 'Manual',
    documentFile: ''
  });


  // Assignment state
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<string>>(new Set());
  const [assignmentMode, setAssignmentMode] = useState<boolean>(false);
  const [showAssignmentDrawer, setShowAssignmentDrawer] = useState<boolean>(false);
  // Use assignees from React Query instead of local state
  const availableAssignees = assigneesData || [];

  // Equipment editing state
  const [showEquipmentEditDrawer, setShowEquipmentEditDrawer] = useState(false);
  const [currentEquipment, setCurrentEquipment] = useState<any | null>(null);
  // Selection state
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [makeAndModels, setMakeAndModels] = useState<any[]>([]);
  const [dropdownMakeAndModels, setDropdownMakeAndModels] = useState<any[]>([]);
  const [selectedAssetForCheckpoints, setSelectedAssetForCheckpoints] = useState<ProjectAsset | null>(null);
  const [showCheckpointsDrawer, setShowCheckpointsDrawer] = useState(false);
  const [showProjectAssets, setShowProjectAssets] = useState(true);

  // Checkpoint management state (asset checkpoints only; shown when audit is selected)
  const [showAssetCheckpointsDrawer, setShowAssetCheckpointsDrawer] = useState(false);
  const [selectedAssetForAuditCheckpoints, setSelectedAssetForAuditCheckpoints] = useState<ProjectAsset | null>(null);
  const [assetCheckpoints, setAssetCheckpoints] = useState<any[]>([]);
  const [loadingAssetCheckpoints, setLoadingAssetCheckpoints] = useState(false);
  const [expandedAssetCheckpoint, setExpandedAssetCheckpoint] = useState<string | null>(null);
  const [editingAssetCheckpoint, setEditingAssetCheckpoint] = useState<string | null>(null);
  const [showCreateAssetCheckpoint, setShowCreateAssetCheckpoint] = useState(false);

  // Project-level checkpoints for the selected audit (not tied to a specific asset)
  const [showProjectCheckpointsDrawer, setShowProjectCheckpointsDrawer] = useState(false);
  const [projectCheckpoints, setProjectCheckpoints] = useState<any[]>([]);
  const [loadingProjectCheckpoints, setLoadingProjectCheckpoints] = useState(false);
  const [showCreateProjectCheckpoint, setShowCreateProjectCheckpoint] = useState(false);

  // Active checkpoint counts for the selected audit — badges the per-asset "Checkpoints"
  // buttons and the "Project Checkpoints" button (mirrors the "N eq" equipment badge).
  const [assetActiveCheckpointCounts, setAssetActiveCheckpointCounts] = useState<Map<string, number>>(new Map());
  const [projectActiveCheckpointCount, setProjectActiveCheckpointCount] = useState<number>(0);

  // Search + execution-type filters for the checkpoint list inside the Asset/Project
  // Checkpoints drawers. Only one drawer is open at a time, so these are shared.
  const [checkpointSearchQuery, setCheckpointSearchQuery] = useState('');
  const [checkpointExecutionTypeFilter, setCheckpointExecutionTypeFilter] = useState<'' | 'manual' | 'visual' | 'hearing'>('');
  const resetCheckpointFilters = () => {
    setCheckpointSearchQuery('');
    setCheckpointExecutionTypeFilter('');
  };
  const filterCheckpointList = useCallback((list: any[]) => {
    return list.filter((cp) => {
      const matchesSearch = !checkpointSearchQuery.trim() ||
        (cp.name || '').toLowerCase().includes(checkpointSearchQuery.trim().toLowerCase());
      const matchesExecutionType = !checkpointExecutionTypeFilter ||
        cp.checkpointMeta?.executionType === checkpointExecutionTypeFilter;
      return matchesSearch && matchesExecutionType;
    });
  }, [checkpointSearchQuery, checkpointExecutionTypeFilter]);

  // Checkpoint Master entries that apply to this asset/project but haven't been pulled
  // into the current audit (e.g. they're flagged for a different audit type). Shown as
  // inactive "available to add" rows; toggling one on materializes a real
  // AmsAuditCheckpoint for this audit from that master, rather than duplicating it.
  const [availableAssetCheckpoints, setAvailableAssetCheckpoints] = useState<any[]>([]);
  const [loadingAvailableAssetCheckpoints, setLoadingAvailableAssetCheckpoints] = useState(false);
  const [availableProjectCheckpoints, setAvailableProjectCheckpoints] = useState<any[]>([]);
  const [loadingAvailableProjectCheckpoints, setLoadingAvailableProjectCheckpoints] = useState(false);
  const [materializingCheckpointId, setMaterializingCheckpointId] = useState<string | null>(null);

  // This project's own Project Type id — scopes which master project-level checkpoints are "available".
  const projectTypeIdForMasters = useMemo(() => {
    const pt = (project as any)?.projectTypeId;
    return typeof pt === 'object' ? (pt?._id || pt?.id) : pt;
  }, [project]);

  const extractMasterId = (cp: any): string => {
    const raw = cp?.masterAmsCheckpointId;
    if (!raw) return '';
    return String(typeof raw === 'object' ? (raw._id ?? raw.id) : raw);
  };

  // asset.masterAssetId is typed as a plain string, but some API responses populate
  // it as an object ({_id, name, ...}) — extract the id defensively either way.
  const extractMasterAssetId = (asset: any): string | undefined => {
    const raw = asset?.masterAssetId;
    if (!raw) return undefined;
    return String(typeof raw === 'object' ? (raw._id ?? raw.id) : raw);
  };

  // Which audit type(s) a Checkpoint Master entry is applicable to — shown on "available
  // to add" rows so it's clear whether it's missing from this audit because it's meant
  // for a different audit type, or just hasn't been synced in yet.
  const [auditTypeIdToName, setAuditTypeIdToName] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    auditTypeService.getAllActiveAuditTypes()
      .then((types) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        types.forEach((t) => { map[t._id] = t.name; });
        setAuditTypeIdToName(map);
      })
      .catch((error) => {
        console.error('Failed to fetch audit types:', error);
      });
    return () => { cancelled = true; };
  }, []);
  const getApplicableAuditTypeLabels = (checkpointMeta: any): string[] => {
    if (!checkpointMeta?.applicableAuditTypeIds) return [];
    return checkpointMeta.applicableAuditTypeIds
      .map((id: string) => auditTypeIdToName[id])
      .filter(Boolean);
  };
  // selectedAudit.auditTypeId is a populated { _id, name } object, or occasionally a
  // bare id string.
  const getSelectedAuditTypeId = (audit: any): string => {
    if (!audit) return '';
    // The AuditType master serializes with the `id` virtual (not `_id`), so read both.
    return typeof audit.auditTypeId === 'string' ? audit.auditTypeId : (audit.auditTypeId?._id || audit.auditTypeId?.id || '');
  };
  const getSelectedAuditTypeLabel = (audit: any): string => {
    if (!audit) return '';
    if (typeof audit.auditTypeId === 'string') {
      return auditTypeIdToName[audit.auditTypeId] || audit.auditTypeId;
    }
    return audit.auditTypeId?.name || '';
  };

  const fetchAvailableAssetCheckpoints = useCallback(async (masterAssetId: string, currentAuditCheckpoints: any[]) => {
    if (!masterAssetId) {
      setAvailableAssetCheckpoints([]);
      return;
    }
    setLoadingAvailableAssetCheckpoints(true);
    try {
      const response = await amsCheckpointService.getAmsCheckpoints({
        'checkpoint-type': 'asset',
        'asset-id': masterAssetId,
        status: 'active',
      });
      const masterCheckpoints = response.data || [];
      const alreadyMaterializedIds = new Set(currentAuditCheckpoints.map(extractMasterId).filter(Boolean));
      setAvailableAssetCheckpoints(
        masterCheckpoints.filter((mc: any) => !alreadyMaterializedIds.has(String(mc._id || mc.id)))
      );
    } catch (error) {
      console.error('Failed to fetch available asset checkpoints:', error);
    } finally {
      setLoadingAvailableAssetCheckpoints(false);
    }
  }, []);

  const fetchAvailableProjectCheckpoints = useCallback(async (currentAuditCheckpoints: any[]) => {
    if (!projectTypeIdForMasters) {
      setAvailableProjectCheckpoints([]);
      return;
    }
    setLoadingAvailableProjectCheckpoints(true);
    try {
      const response = await amsCheckpointService.getAmsCheckpoints({
        'checkpoint-type': 'project',
        'project-type-id': projectTypeIdForMasters,
        status: 'active',
      });
      const masterCheckpoints = response.data || [];
      const alreadyMaterializedIds = new Set(currentAuditCheckpoints.map(extractMasterId).filter(Boolean));
      setAvailableProjectCheckpoints(
        masterCheckpoints.filter((mc: any) => !alreadyMaterializedIds.has(String(mc._id || mc.id)))
      );
    } catch (error) {
      console.error('Failed to fetch available project checkpoints:', error);
    } finally {
      setLoadingAvailableProjectCheckpoints(false);
    }
  }, [projectTypeIdForMasters]);

  // Master data creation state
  const [showManufacturerCreator, setShowManufacturerCreator] = useState(false);
  const [showMakeAndModelCreator, setShowMakeAndModelCreator] = useState(false);
  const [selectedManufacturerForModel, setSelectedManufacturerForModel] = useState<string>('');

  // String grouping state
  const [stringGroupingState, setStringGroupingState] = useState<{
    open: boolean;
    totalModules: number;
    parentType: string | null;
    parentId: string | null;
    suggestedStrings: Array<{ stringName: string; moduleCount: number }>;
  }>({
    open: false,
    totalModules: 0,
    parentType: null,
    parentId: null,
    suggestedStrings: []
  });

  // Master assets data - stores full asset info including code and category
  const [masterAssets, setMasterAssets] = useState<Asset[]>([]);

  // Tree view enhancements with custom hooks
  const { zoomLevel, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut } = useTreeZoom();

  const { scrollContainerRef } = useTreeNavigation();

  // Filter panel visibility
  const [showFilterPanel, setShowFilterPanel] = useState(false);







  // Child Selection State
  const [childSelectionState, setChildSelectionState] = useState<{
    open: boolean;
    parentId: string | null;
    parentType: string | null;
    warningOpen: boolean;
    selectedAsset: any | null;
  }>({
    open: false,
    parentId: null,
    parentType: null,
    warningOpen: false,
    selectedAsset: null
  });


  // Confirmation Drawer State
  const [confirmationState, setConfirmationState] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant: 'danger' | 'warning' | 'info' | 'success';
    loading: boolean;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'danger',
    loading: false,
  });

  // Bulk Equipment Creation Loading State
  const [isBulkCreatingEquipment, setIsBulkCreatingEquipment] = useState(false);

  const treeContainerRef = useRef<HTMLDivElement | null>(null);

  // Fetch equipment is now handled by React Query
  // Keep a refetch function for manual refresh
  const fetchEquipment = async () => {
    await refetchEquipment();
  };

  // Handle audit selection changes - close checkpoint drawers when audit changes
  useEffect(() => {
    if (selectedAuditId) {
      setShowAssetCheckpointsDrawer(false);
      setSelectedAssetForAuditCheckpoints(null);
      setAssetCheckpoints([]);
      setAvailableAssetCheckpoints([]);
      setShowProjectCheckpointsDrawer(false);
      setProjectCheckpoints([]);
      setAvailableProjectCheckpoints([]);
      resetCheckpointFilters();
    }
  }, [selectedAuditId]);

  // Fetch Asset Checkpoints for a specific asset
  const fetchAssetCheckpoints = useCallback(async (assetId: string, masterAssetId?: string) => {
    if (!selectedAuditId) return;
    setLoadingAssetCheckpoints(true);
    try {
      const checkpoints = await amsAuditCheckpointService.getAuditCheckpoints(
        selectedAuditId,
        'asset',
        assetId
      );
      setAssetCheckpoints(checkpoints);
      if (masterAssetId) {
        fetchAvailableAssetCheckpoints(masterAssetId, checkpoints);
      }
    } catch (error: any) {
      console.error('Failed to fetch asset checkpoints:', error);
      toast.error('Failed to load asset checkpoints');
    } finally {
      setLoadingAssetCheckpoints(false);
    }
  }, [selectedAuditId, fetchAvailableAssetCheckpoints]);

  // Fetch Project-level Checkpoints (checkpointType 'project', not tied to any asset) for the selected audit
  const fetchProjectCheckpoints = useCallback(async () => {
    if (!selectedAuditId) return;
    setLoadingProjectCheckpoints(true);
    try {
      const checkpoints = await amsAuditCheckpointService.getAuditCheckpoints(
        selectedAuditId,
        'project'
      );
      setProjectCheckpoints(checkpoints);
      fetchAvailableProjectCheckpoints(checkpoints);
    } catch (error: any) {
      console.error('Failed to fetch project checkpoints:', error);
      toast.error('Failed to load project checkpoints');
    } finally {
      setLoadingProjectCheckpoints(false);
    }
  }, [selectedAuditId, fetchAvailableProjectCheckpoints]);

  // Fetch active-checkpoint counts for the selected audit, grouped by asset (plus a
  // project-level total) — badges the per-asset "Checkpoints" buttons and the
  // "Project Checkpoints" button. Omitting `status` returns active-only checkpoints
  // (activeDefaultPlugin default), and omitting `checkpointType` returns both kinds
  // in one call.
  const fetchActiveCheckpointCounts = useCallback(async () => {
    if (!selectedAuditId) {
      setAssetActiveCheckpointCounts(new Map());
      setProjectActiveCheckpointCount(0);
      return;
    }
    try {
      const checkpoints = await amsAuditCheckpointService.getAuditCheckpoints(selectedAuditId);
      const assetCounts = new Map<string, number>();
      let projectCount = 0;
      checkpoints.forEach((cp: any) => {
        if (cp.checkpointType === 'project') {
          projectCount++;
          return;
        }
        const assetId = typeof cp.amsProjectAssetId === 'object' && cp.amsProjectAssetId != null
          ? String((cp.amsProjectAssetId as any)._id ?? (cp.amsProjectAssetId as any).id)
          : cp.amsProjectAssetId != null ? String(cp.amsProjectAssetId) : '';
        if (assetId) {
          assetCounts.set(assetId, (assetCounts.get(assetId) || 0) + 1);
        }
      });
      setAssetActiveCheckpointCounts(assetCounts);
      setProjectActiveCheckpointCount(projectCount);
    } catch (error) {
      console.error('Failed to fetch checkpoint counts:', error);
    }
  }, [selectedAuditId]);

  useEffect(() => {
    fetchActiveCheckpointCounts();
  }, [fetchActiveCheckpointCounts]);

  // Handle opening asset checkpoints drawer for a specific asset
  const handleOpenAssetCheckpoints = (asset: ProjectAsset) => {
    if (!selectedAuditId) {
      toast.error('Please select an audit first');
      return;
    }
    setSelectedAssetForAuditCheckpoints(asset);
    setShowAssetCheckpointsDrawer(true);
    fetchAssetCheckpoints(asset._id || asset.id || '', extractMasterAssetId(asset));
  };

  // Handle opening project-level checkpoints drawer
  const handleOpenProjectCheckpoints = () => {
    if (!selectedAuditId) {
      toast.error('Please select an audit first');
      return;
    }
    setShowProjectCheckpointsDrawer(true);
    fetchProjectCheckpoints();
  };

  // Pull a Checkpoint Master entry (flagged for a different audit type, or otherwise
  // not yet materialized) into the current audit as a real, active AmsAuditCheckpoint.
  const handleMaterializeAssetCheckpoint = async (masterCheckpoint: any) => {
    if (!selectedAuditId || !selectedAssetForAuditCheckpoints) return;
    const masterId = String(masterCheckpoint._id || masterCheckpoint.id);
    setMaterializingCheckpointId(masterId);
    try {
      await amsAuditCheckpointService.createCheckpoint({
        amsProjectAuditId: selectedAuditId,
        amsProjectAssetId: selectedAssetForAuditCheckpoints._id || selectedAssetForAuditCheckpoints.id || '',
        masterAmsCheckpointId: masterId,
        name: masterCheckpoint.name,
        checkpointType: 'asset',
        checkpointMeta: masterCheckpoint.checkpointMeta,
      });
      toast.success(`"${masterCheckpoint.name}" added to this audit`);
      fetchAssetCheckpoints(
        selectedAssetForAuditCheckpoints._id || selectedAssetForAuditCheckpoints.id || '',
        extractMasterAssetId(selectedAssetForAuditCheckpoints)
      );
      fetchActiveCheckpointCounts();
      onCheckpointsUpdated?.();
    } catch (error: any) {
      console.error('Failed to add checkpoint to audit:', error);
      toast.error(error?.response?.data?.message || 'Failed to add checkpoint to this audit');
    } finally {
      setMaterializingCheckpointId(null);
    }
  };

  const handleMaterializeProjectCheckpoint = async (masterCheckpoint: any) => {
    if (!selectedAuditId) return;
    const masterId = String(masterCheckpoint._id || masterCheckpoint.id);
    setMaterializingCheckpointId(masterId);
    try {
      await amsAuditCheckpointService.createCheckpoint({
        amsProjectAuditId: selectedAuditId,
        masterAmsCheckpointId: masterId,
        name: masterCheckpoint.name,
        checkpointType: 'project',
        checkpointMeta: masterCheckpoint.checkpointMeta,
      });
      toast.success(`"${masterCheckpoint.name}" added to this audit`);
      fetchProjectCheckpoints();
      fetchActiveCheckpointCounts();
      onCheckpointsUpdated?.();
    } catch (error: any) {
      console.error('Failed to add checkpoint to audit:', error);
      toast.error(error?.response?.data?.message || 'Failed to add checkpoint to this audit');
    } finally {
      setMaterializingCheckpointId(null);
    }
  };

  // Format date time
  const formatDateTime = (dateString: string | Date) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Handle checkpoint click (expand/collapse) - asset checkpoints only
  const handleCheckpointClick = useCallback((checkpointId: string, _type: 'project' | 'asset') => {
    const id = String(checkpointId);
    if (!id || id === 'undefined' || id === 'null') {
      console.error('Invalid checkpoint ID:', checkpointId);
      return;
    }
    setExpandedAssetCheckpoint((prev) => {
      if (prev === id) return null;
      return id;
    });
    setEditingAssetCheckpoint(null);
  }, []);

  // Handle edit click (asset checkpoints only)
  const handleEditCheckpoint = (checkpointId: string, _type: 'project' | 'asset', e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAssetCheckpoint(checkpointId);
    setExpandedAssetCheckpoint(checkpointId);
  };

  // Handle toggle status
  const [togglingCheckpointId, setTogglingCheckpointId] = useState<string | null>(null);
  const handleToggleCheckpointStatus = async (checkpoint: any, type: 'project' | 'asset', e: React.MouseEvent) => {
    e.stopPropagation();
    const rawId = (checkpoint as any)._id || (checkpoint as any).id;
    if (!rawId) {
      console.error('Checkpoint missing ID for toggle:', checkpoint);
      toast.error('Checkpoint ID is missing. Please refresh the page.');
      return;
    }
    const checkpointId = String(rawId);
    if (checkpointId === 'undefined' || checkpointId === 'null' || checkpointId.trim() === '') {
      console.error('Invalid checkpoint ID format for toggle:', checkpointId);
      toast.error('Invalid checkpoint ID format. Please refresh the page.');
      return;
    }
    setTogglingCheckpointId(checkpointId);
    try {
      const newStatus = checkpoint.status === 'active' ? 'inactive' : 'active';
      await amsAuditCheckpointService.updateCheckpoint(checkpointId, { status: newStatus });
      toast.success(`Checkpoint ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      if (type === 'project') {
        fetchProjectCheckpoints();
      } else if (selectedAssetForAuditCheckpoints) {
        fetchAssetCheckpoints(selectedAssetForAuditCheckpoints._id || selectedAssetForAuditCheckpoints.id || '', extractMasterAssetId(selectedAssetForAuditCheckpoints));
      }
      fetchActiveCheckpointCounts();
      onCheckpointsUpdated?.();
    } catch (error: any) {
      console.error('Failed to toggle checkpoint status:', error);
      toast.error(error?.response?.data?.message || 'Failed to update checkpoint status');
    } finally {
      setTogglingCheckpointId(null);
    }
  };

  // Load master data for equipment editing
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [mfgData, mmData, assetsData] = await Promise.all([
          manufacturerService.manufacturerService.getManufacturers({ limit: 1000 }),
          makeAndModelService.getMakeAndModels({ limit: 1000 }),
          assetService.getAllAssets()
        ]);
        setManufacturers(mfgData.data || []);
        setMakeAndModels(mmData.data || []);
        setMasterAssets(assetsData || []);
      } catch (error) {
        console.error('Failed to load master data:', error);
      }
    };
    loadMasterData();
  }, []);

  // Fetch make and models dynamically when manufacturer changes in add modal
  useEffect(() => {
    const fetchModels = async () => {
      if (addModalState.open && addModalState.manufacturer) {
        try {
          const models = await makeAndModelService.getMakeAndModelsForDropdown(addModalState.manufacturer);
          setDropdownMakeAndModels(models || []);
        } catch (error) {
          console.error('Failed to fetch make and models:', error);
          setDropdownMakeAndModels([]);
        }
      } else {
        setDropdownMakeAndModels([]);
      }
    };

    fetchModels();
  }, [addModalState.manufacturer, addModalState.open]);

  const handleAddBulkDocument = () => {
    if (!newBulkDocument.documentName || !newBulkDocument.documentFile) {
      toast.error('Please fill in document name and file URL');
      return;
    }
    setAddModalState(prev => ({
      ...prev,
      documents: [...prev.documents, { ...newBulkDocument }]
    }));
    setNewBulkDocument({ documentName: '', documentType: 'Manual', documentFile: '' });
  };

  const handleRemoveBulkDocument = (index: number) => {
    setAddModalState(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Handlers for manufacturer and make/model creation
  const handleManufacturerCreated = async (newManufacturer: any) => {
    try {
      // Refresh manufacturers list
      const mfgData = await manufacturerService.manufacturerService.getManufacturers({ limit: 1000 });
      setManufacturers(mfgData.data || []);

      // Auto-select the newly created manufacturer
      const manufacturerId = newManufacturer._id || newManufacturer.id;
      setAddModalState({
        ...addModalState,
        manufacturer: manufacturerId
      });

      setShowManufacturerCreator(false);
      toast.success(`Manufacturer "${newManufacturer.name}" created successfully`);
    } catch (error) {
      console.error('Failed to refresh manufacturers:', error);
      toast.error('Manufacturer created but failed to refresh list');
      setShowManufacturerCreator(false);
    }
  };

  const handleMakeAndModelCreated = async (newModel: any) => {
    try {
      // Refresh make & models list for current manufacturer
      if (addModalState.manufacturer) {
        const models = await makeAndModelService.getMakeAndModelsForDropdown(addModalState.manufacturer);
        setDropdownMakeAndModels(models || []);
      }

      // Auto-select the newly created model
      const modelId = newModel._id || newModel.id;
      setAddModalState({
        ...addModalState,
        makeAndModelId: modelId,
        makeAndModel: newModel.name
      });

      setShowMakeAndModelCreator(false);
      toast.success(`Make & Model "${newModel.name}" created successfully`);
    } catch (error) {
      console.error('Failed to refresh make & models:', error);
      toast.error('Make & Model created but failed to refresh list');
      setShowMakeAndModelCreator(false);
    }
  };

  // Helper to normalize IDs for comparison
  const normalizeId = (id: any): string | null => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return String(id._id);
    if (typeof id === 'object' && id.id) return String(id.id);
    return String(id);
  };

  const visualEquipmentList = useMemo<VisualEquipment[]>(() => {
    return equipmentList.map(eq => {
      // Look up engineer name if missing
      let engineerName = eq.siteEngineerName;
      if (eq.siteEngineerId && !engineerName && availableAssignees.length > 0) {
        const engineer = availableAssignees.find(a =>
          normalizeId(a._id) === normalizeId(eq.siteEngineerId)
        );
        if (engineer) {
          engineerName = engineer.name;
        }
      }

      // Look up supervisor name if missing
      let supervisorName = eq.siteSupervisorName;
      if (eq.siteSupervisorId && !supervisorName && availableAssignees.length > 0) {
        const supervisor = availableAssignees.find(a =>
          normalizeId(a._id) === normalizeId(eq.siteSupervisorId)
        );
        if (supervisor) {
          supervisorName = supervisor.name;
        }
      }

      // Look up asset category from master assets
      // First find the project asset, then the master asset
      const projectAsset = projectAssets.find(pa =>
        pa._id === eq.projectAssetId || pa._id === eq.assetId
      );
      let assetCategory: string | undefined;
      if (projectAsset) {
        const masterAsset = masterAssets.find(ma =>
          (ma._id === projectAsset.masterAssetId) || (ma.id === projectAsset.masterAssetId)
        );
        assetCategory = masterAsset?.architectureRules?.category;
      }

      return {
        id: eq._id || eq.id,
        assetType: eq.assetType || eq.hierarchyLevel || 'Other',
        parentEquipmentId: eq.parentId || eq.parentEquipmentId || null,
        childEquipmentIds: equipmentList
          .filter(child => {
            const pId = child.parentId || child.parentEquipmentId;
            return pId === (eq._id || eq.id);
          })
          .map(child => child._id || child.id),
        name: eq.equipmentName || eq.name || 'Unnamed',
        // Asset category from master
        assetCategory,
        // Assignment info
        isAssigned: !!(eq.siteEngineerId || eq.siteSupervisorId),
        assignedTo: eq.siteEngineerId ? {
          id: eq.siteEngineerId,
          name: engineerName || 'Unknown Engineer',
          role: 'Site Engineer'
        } : eq.siteSupervisorId ? {
          id: eq.siteSupervisorId,
          name: supervisorName || 'Unknown Supervisor',
          role: 'Site Supervisor'
        } : undefined,
        siteSupervisorId: eq.siteSupervisorId,
        siteSupervisorName: supervisorName
      };
    });
  }, [equipmentList, availableAssignees, projectAssets, masterAssets]);

  const hasInitialExpanded = useRef(false);
  useEffect(() => {
    if (visualEquipmentList.length > 0 && !hasInitialExpanded.current) {
      setExpandedNodes(new Set(visualEquipmentList.map(e => e.id)));
      hasInitialExpanded.current = true;
    }
  }, [visualEquipmentList.length]);

  // ===== Assignment Helper Functions =====

  /**
   * Get all descendant equipment IDs recursively
   */
  const getAllDescendants = (equipmentId: string): string[] => {
    const equipment = visualEquipmentList.find(e => e.id === equipmentId);
    if (!equipment || equipment.childEquipmentIds.length === 0) {
      return [];
    }

    const descendants: string[] = [];
    const queue = [...equipment.childEquipmentIds];

    while (queue.length > 0) {
      const childId = queue.shift()!;
      descendants.push(childId);

      const child = visualEquipmentList.find(e => e.id === childId);
      if (child && child.childEquipmentIds.length > 0) {
        queue.push(...child.childEquipmentIds);
      }
    }

    return descendants;
  };

  /**
   * Toggle equipment selection with hierarchical logic
   */
  const toggleEquipmentSelection = (equipmentId: string) => {
    const newSelection = new Set(selectedEquipmentIds);

    if (newSelection.has(equipmentId)) {
      // Deselect: remove this equipment and all descendants
      newSelection.delete(equipmentId);
      const descendants = getAllDescendants(equipmentId);
      descendants.forEach(id => newSelection.delete(id));
    } else {
      // Select: add this equipment and all descendants
      newSelection.add(equipmentId);
      const descendants = getAllDescendants(equipmentId);
      descendants.forEach(id => newSelection.add(id));
    }

    setSelectedEquipmentIds(newSelection);
  };

  /**
   * Select all equipment
   */
  const selectAllEquipment = () => {
    const allIds = new Set(visualEquipmentList.map(e => e.id));
    setSelectedEquipmentIds(allIds);
  };

  /**
   * Clear all selections
   */
  const clearSelection = () => {
    setSelectedEquipmentIds(new Set());
  };

  /**
   * Check if equipment or any of its descendants are assigned
   */
  const isEquipmentOrDescendantsAssigned = (equipmentId: string): boolean => {
    const equipment = visualEquipmentList.find(e => e.id === equipmentId);

    // Check if the equipment itself is assigned
    if (equipment?.isAssigned) {
      return true;
    }

    // Check if any descendant is assigned
    const descendants = getAllDescendants(equipmentId);
    return descendants.some(descendantId => {
      const descendant = visualEquipmentList.find(e => e.id === descendantId);
      return descendant?.isAssigned;
    });
  };

  // ===== Rejection Helper Functions =====

  /**
   * Get rejection/update info for an asset.
   * - Rejected: checkpoints still rejected (need action).
   * - Updated: checkpoints that were rejected and user has changed them (show "Updated" tag).
   */
  // AMS per-asset rejection is sourced from the AMS audit approval (see
  // `amsRejectedAssetIds` / `updatedAssetIds`), not the OMS project-approval
  // endpoint (which has no record for AMS projects). Kept as a stable shape so
  // the render sites that read `.hasRejections` / `.hasUpdated` stay unchanged.
  const getAssetRejectionInfo = (_assetId: string) => ({
    hasRejections: false,
    rejectedCount: 0,
    rejectedCheckpoints: [] as unknown[],
    hasUpdated: false,
    modifiedCount: 0,
  });

  /** Asset IDs with rejected checkpoints in the selected audit (AMS). Manager can see which assets need updates. */
  const amsRejectedAssetIds = useMemo(() => {
    const set = new Set<string>();
    if (!selectedAuditApproval?.checkpointApprovals?.assetCheckpoints) return set;
    for (const cp of selectedAuditApproval.checkpointApprovals.assetCheckpoints) {
      if (cp.status !== 'rejected') continue;
      const raw = cp.amsProjectAssetId;
      const id = typeof raw === 'object' && raw != null ? (raw._id ?? (raw as any).id) : raw;
      if (id != null) set.add(String(id));
    }
    return set;
  }, [selectedAuditApproval]);

  const assetsSectionRejected = projectApprovalSections?.assetsAndEquipment?.status === 'rejected';

  /** Per-checkpoint approval (status, reason) for the current asset in the right drawer. Used to show rejected reason and Updated tag. */
  const assetCheckpointApprovalMap = useMemo(() => {
    const map = new Map<string, { status: string; approverComments?: string }>();
    const assetId = selectedAssetForAuditCheckpoints?._id ?? (selectedAssetForAuditCheckpoints as any)?.id;
    if (!assetId || !selectedAuditApproval?.checkpointApprovals?.assetCheckpoints) return map;
    const assetIdStr = String(assetId);
    for (const cp of selectedAuditApproval.checkpointApprovals.assetCheckpoints) {
      const cpAssetId = typeof cp.amsProjectAssetId === 'object' && cp.amsProjectAssetId != null
        ? String((cp.amsProjectAssetId as any)._id ?? (cp.amsProjectAssetId as any).id)
        : cp.amsProjectAssetId != null ? String(cp.amsProjectAssetId) : '';
      if (cpAssetId !== assetIdStr) continue;
      const cpId = typeof cp.checkpointId === 'object' && cp.checkpointId != null
        ? String((cp.checkpointId as any)._id ?? (cp.checkpointId as any).id)
        : cp.checkpointId != null ? String(cp.checkpointId) : '';
      if (cpId) map.set(cpId, { status: cp.status, approverComments: cp.approverComments });
    }
    return map;
  }, [selectedAuditApproval, selectedAssetForAuditCheckpoints]);

  /** Per-audit: "auditId:checkpointId" — user updated this checkpoint (after rejection) in this audit; show "Updated" in drawer. */
  const [updatedCheckpointIds, setUpdatedCheckpointIds] = useState<Set<string>>(new Set());
  /** Per-audit: "auditId:assetId" — user updated at least one rejected checkpoint on this asset in this audit; asset card shows "Updated". */
  const [updatedAssetIds, setUpdatedAssetIds] = useState<Set<string>>(new Set());

  /** Project-level checkpoint IDs with 'rejected' status in the selected audit (AMS). */
  const amsRejectedProjectCheckpointIds = useMemo(() => {
    const set = new Set<string>();
    if (!selectedAuditApproval?.checkpointApprovals?.projectCheckpoints) return set;
    for (const cp of selectedAuditApproval.checkpointApprovals.projectCheckpoints) {
      if (cp.status !== 'rejected') continue;
      const cpId = typeof cp.checkpointId === 'object' && cp.checkpointId != null
        ? String((cp.checkpointId as any)._id ?? (cp.checkpointId as any).id)
        : cp.checkpointId != null ? String(cp.checkpointId) : '';
      if (cpId) set.add(cpId);
    }
    return set;
  }, [selectedAuditApproval]);

  /** Per-checkpoint approval (status, reason) for project-level checkpoints. Used to show rejected reason and Updated tag. */
  const projectCheckpointApprovalMap = useMemo(() => {
    const map = new Map<string, { status: string; approverComments?: string }>();
    if (!selectedAuditApproval?.checkpointApprovals?.projectCheckpoints) return map;
    for (const cp of selectedAuditApproval.checkpointApprovals.projectCheckpoints) {
      const cpId = typeof cp.checkpointId === 'object' && cp.checkpointId != null
        ? String((cp.checkpointId as any)._id ?? (cp.checkpointId as any).id)
        : cp.checkpointId != null ? String(cp.checkpointId) : '';
      if (cpId) map.set(cpId, { status: cp.status, approverComments: cp.approverComments });
    }
    return map;
  }, [selectedAuditApproval]);

  /** Count of project-level checkpoints still rejected (not yet updated) in the selected audit — badges the "Project Checkpoints" button. */
  const rejectedProjectCheckpointCount = useMemo(() => {
    let count = 0;
    amsRejectedProjectCheckpointIds.forEach((id) => {
      const isUpdated = !!selectedAuditId && updatedCheckpointIds.has(`${selectedAuditId}:${id}`);
      if (!isUpdated) count++;
    });
    return count;
  }, [amsRejectedProjectCheckpointIds, updatedCheckpointIds, selectedAuditId]);

  /**
   * Handle removing/deactivating a project asset
   */
  const handleRemoveProjectAsset = async (asset: ProjectAsset) => {
    // Check if asset has associated equipment
    const equipmentCount = equipmentList.filter(
      eq => eq.projectAssetId === asset._id || eq.assetId === asset._id
    ).length;

    if (equipmentCount > 0) {
      toast.error(`Cannot remove asset "${asset.name}" as it has ${equipmentCount} associated equipment.`);
      return;
    }

    // Show confirmation drawer
    setConfirmationState({
      open: true,
      title: 'Remove Project Asset',
      message: `Are you sure you want to remove "${asset.name}" from this project?\n\nThis action will deactivate the asset.`,
      variant: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmationState(prev => ({ ...prev, loading: true }));
        try {
          const assetId = asset._id || asset.id;
          await amsProjectAssetService.updateAmsProjectAsset(assetId, { status: 'inactive' });
          toast.success(`Asset "${asset.name}" has been removed from the project.`);

          // Refresh the project assets list by calling the parent's update handler
          if (onEquipmentUpdate) {
            onEquipmentUpdate();
          }

          // Close confirmation drawer
          setConfirmationState(prev => ({ ...prev, open: false, loading: false }));
        } catch (error: any) {
          console.error('Failed to remove project asset:', error);
          toast.error(error?.response?.data?.message || 'Failed to remove asset. Please try again.');
          setConfirmationState(prev => ({ ...prev, loading: false }));
        }
      },
    });
  };

  /**
   * Get resource rejection info for equipment.
   * Equipment-level resource rejections are an OMS resource-assignment concept;
   * AMS audits don't use that model, so there's nothing to surface here.
   */
  const getEquipmentResourceRejection = (_equipmentId: string) => ({
    isRejected: false,
    rejectedResources: [] as unknown[],
  });



  // Ghost nodes disabled - using only "Add Child" button
  const ghostNodes = useMemo<VisualEquipment[]>(() => [], []);

  // Layout: dynamic placement with 20px spacing (no drag/move)
  const layoutNodes = useMemo(
    () =>
      calculateTreeLayout({
        visualEquipmentList,
        ghostNodes,
        expandedNodes
      }),
    [visualEquipmentList, ghostNodes, expandedNodes]
  );

  const { NODE_WIDTH, MODULE_WIDTH, MODULE_HEIGHT, MODULE_SPACING, MODULE_GRID_PADDING, MODULE_GRID_GAP_BG } = LAYOUT;

  const handleDeleteEquipment = async (id: string) => {
    // Check if equipment or its descendants are assigned
    const equipment = visualEquipmentList.find(e => e.id === id);

    if (!equipment) {
      toast.error("Equipment not found");
      return;
    }

    // Check if this equipment is assigned
    if (equipment.isAssigned) {
      toast.error(`Cannot delete "${equipment.name}" - it is assigned to ${equipment.assignedTo?.name}. Please unassign it first.`);
      return;
    }

    // Check if any descendant is assigned
    const descendants = getAllDescendants(id);
    const assignedDescendants = descendants
      .map(descId => visualEquipmentList.find(e => e.id === descId))
      .filter(desc => desc?.isAssigned);

    if (assignedDescendants.length > 0) {
      const descendantNames = assignedDescendants.map(d => d!.name).join(', ');
      toast.error(
        `Cannot delete "${equipment.name}" - it has assigned child equipment: ${descendantNames}. Please unassign them first.`
      );
      return;
    }

    // Show confirmation drawer
    const descendantCount = descendants.length;
    const confirmMessage = descendantCount > 0
      ? `Are you sure you want to delete "${equipment.name}" and its ${descendantCount} child equipment?\n\nThis action cannot be undone.`
      : `Are you sure you want to delete "${equipment.name}"?\n\nThis action cannot be undone.`;

    setConfirmationState({
      open: true,
      title: 'Delete Equipment',
      message: confirmMessage,
      variant: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmationState(prev => ({ ...prev, loading: true }));
        try {
          await amsEquipmentService.deleteEquipment(id);
          toast.success("Equipment deleted successfully");
          // Invalidate queries to force fresh data fetch
          await queryClient.invalidateQueries({ queryKey: ['ams-equipment', projectId] });
          fetchAssignees(); // Refresh assignees as well
          if (onEquipmentUpdate) onEquipmentUpdate();

          // Close confirmation drawer
          setConfirmationState(prev => ({ ...prev, open: false, loading: false }));
        } catch (error) {
          console.error("Delete failed", error);
          toast.error("Failed to delete equipment");
          setConfirmationState(prev => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Equipment editing handlers – open right-side Equipment Details drawer
  const handleEquipmentClick = (equipment: any) => {
    if (assignmentMode) return; // Don't open edit drawer in assignment mode

    const id = equipment.id || equipment._id;
    // Use full equipment from list when available so drawer has all fields immediately
    const fromList = id ? equipmentList.find((e: any) => (e._id || e.id) === id) : undefined;
    const initialEquipment = fromList || equipment;
    setCurrentEquipment(initialEquipment);
    setShowEquipmentEditDrawer(true);
  };

  const handleEquipmentEditSuccess = async () => {
    // Invalidate equipment query cache to force fresh data fetch
    await queryClient.invalidateQueries({ queryKey: ['ams-equipment', projectId] });
    if (onEquipmentUpdate) onEquipmentUpdate();
  };

  const openAddModal = (ghost: any) => {
    setAddModalState({
      open: true,
      ghost,
      quantity: ghost.config?.unitsPerParent || 1,
      namingPattern: ghost.config?.namingPattern || (ghost.assetType === 'String' ? 'STR-{seq}' : '{asset}-{seq}'),
      documents: []
    });
  };

  // Calculate optimal string distribution for modules
  const calculateStringDistribution = (totalModules: number): Array<{ stringName: string; moduleCount: number }> => {
    const IDEAL_MODULES_PER_STRING = 28;
    const MIN_MODULES_PER_STRING = 24;
    const MAX_MODULES_PER_STRING = 30;

    let modulesPerString = IDEAL_MODULES_PER_STRING;
    let stringCount = Math.ceil(totalModules / modulesPerString);

    // Adjust if distribution is uneven
    if (totalModules % modulesPerString < MIN_MODULES_PER_STRING && totalModules % modulesPerString > 0) {
      // Redistribute to avoid small remainders
      stringCount = Math.floor(totalModules / MIN_MODULES_PER_STRING);
      if (stringCount === 0) stringCount = 1;
      modulesPerString = Math.ceil(totalModules / stringCount);
    }

    const suggestions = [];
    let remaining = totalModules;

    for (let i = 1; i <= stringCount; i++) {
      const count = Math.min(modulesPerString, remaining);
      suggestions.push({
        stringName: `String ${i}`,
        moduleCount: count
      });
      remaining -= count;
    }

    return suggestions;
  };

  const handleBulkCreate = async () => {
    if (!addModalState.ghost) return;
    if (!auditId) { toast.error('Select an audit first'); return; }

    setIsBulkCreatingEquipment(true);
    try {
      const { ghost, quantity } = addModalState;
      const projectId = project._id || project.id;

      // Use projectAssetId from ghost if available, otherwise find/create it
      let projectAsset;
      if (ghost.projectAssetId) {
        // Direct projectAssetId provided (from empty state asset cards)
        projectAsset = projectAssets.find(pa => pa._id === ghost.projectAssetId || pa.id === ghost.projectAssetId);
        if (!projectAsset) {
          throw new Error('Project asset not found');
        }
      } else {
        // Find by masterAssetId (from ghost nodes)
        projectAsset = projectAssets.find(pa => {
          // masterAssetId is typed string but arrives populated from some endpoints
          const paMasterId =
            typeof pa.masterAssetId === 'string'
              ? pa.masterAssetId
              : (pa.masterAssetId as unknown as { _id?: string } | undefined)?._id;
          const ghostMasterId = ghost.masterAssetId;
          return paMasterId === ghostMasterId;
        });
        if (!projectAsset) {
          projectAsset = await amsProjectAssetService.createOrGetAmsProjectAsset(projectId, auditId as string, ghost.masterAssetId);
        }
      }

      // Validate projectAsset before proceeding
      if (!projectAsset) {
        console.error('Project asset not found:', { ghost, projectAssets });
        toast.error('Project asset not found. Please add the asset to your project first.');
        return;
      }

      // Handle both _id and id fields
      const projectAssetId = projectAsset._id || projectAsset.id;
      if (!projectAssetId) {
        console.error('Project asset missing _id or id:', projectAsset);
        toast.error('Invalid project asset. Please try again.');
        return;
      }

      console.log('Creating equipment with projectAsset:', projectAsset);

      // Check if manual string grouping is specified
      if (addModalState.stringCount && addModalState.stringCount > 0) {
        await handleManualStringGrouping();
        return;
      }

      // Check if string grouping suggestion is needed (when PV modules > 10 under inverter)
      const isSolarModule = ghost.assetName?.toLowerCase().includes('module') ||
        ghost.assetName?.toLowerCase().includes('solar') ||
        ghost.assetName?.toLowerCase().includes('panel');
      const parentEquipment = ghost.parentId ? equipmentList.find(e => (e._id || e.id) === ghost.parentId) : null;
      const parentType = parentEquipment?.assetType || ghost.parentType;
      const isInverterParent = parentType && (parentType.toLowerCase().includes('inverter'));

      const shouldSuggestStrings = (
        quantity >= 10 &&
        isSolarModule &&
        isInverterParent
      );

      if (shouldSuggestStrings) {
        // Show string grouping modal
        setStringGroupingState({
          open: true,
          totalModules: quantity,
          parentType: parentType || 'Inverter',
          parentId: ghost.parentId,
          suggestedStrings: calculateStringDistribution(quantity)
        });
        return; // Pause bulk creation until user decides
      }

      // Canonical kind stored on the equipment (GAP_REPORT P2-6): moduleType is
      // authoritative when the asset carries it, name fragments cover the rest.
      const equipmentKind = canonicalEquipmentKind(
        ghost.assetName || ghost.assetType,
        (projectAsset as { moduleType?: string }).moduleType
      );

      // Siblings compared by canonical kind so sequence numbering still lines up
      // with rows stored before canonicalisation (raw asset names).
      const siblings = equipmentList.filter(eq =>
        (eq.parentId === ghost.parentId) &&
        (eq.assetType === ghost.assetType || canonicalEquipmentKind(eq.assetType) === equipmentKind)
      );

      let maxSeq = 0;
      siblings.forEach(s => {
        const match = (s.equipmentName || s.name).match(/-(\d+)$/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxSeq) maxSeq = num;
        }
      });

      // Get naming prefix from master asset code
      // Find the master asset by matching masterAssetId
      const masterAsset = masterAssets.find(a =>
        (a._id === ghost.masterAssetId) || (a.id === ghost.masterAssetId)
      );

      // Use code from project asset if available, then master asset, otherwise fallback
      let typePrefix: string;

      // Check project asset code first (if available)
      if ((projectAsset as any).code) {
        const codeParts = (projectAsset as any).code.split('-');
        typePrefix = codeParts[0].toUpperCase();
      } else if (masterAsset?.code) {
        // Extract prefix from master asset code (e.g., "MOD-001" -> "MOD", "INV" -> "INV")
        const codeParts = masterAsset.code.split('-');
        typePrefix = codeParts[0].toUpperCase();
      } else {
        // Fallback: use 'INV' for String Inverters, otherwise first 3 letters
        const lowerName = ghost.assetName.toLowerCase();
        if (lowerName.includes('invert')) {
          typePrefix = 'INV';
        } else {
          typePrefix = ghost.assetName.substring(0, 3).toUpperCase();
        }
      }

      const parentEquip = equipmentList.find(e => (e._id || e.id) === ghost.parentId);
      const parentName = parentEquip ? (parentEquip.equipmentName || parentEquip.name) : '';

      const itemsToCreate = [];
      for (let i = 1; i <= quantity; i++) {
        const sequence = maxSeq + i;
        const code = `${typePrefix}-${sequence}`;
        const equipmentName = parentName ? `${parentName}-${code}` : code;

        // Base item with required fields
        const item: any = {
          equipmentName,
          assetId: projectAssetId,
          assetType: equipmentKind,
          hierarchyLevel: equipmentKind,
          parentId: ghost.parentId || null,
          projectId,
          projectAssetId: projectAssetId,
          status: 'active',
          statusBadge: 'active'
        };

        // Add optional fields only if they have values to avoid validation errors
        if (addModalState.manufacturer) {
          item.manufacturer = addModalState.manufacturer;
          item.manufacturerId = addModalState.manufacturer;
        }

        if (addModalState.makeAndModel) {
          item.makeAndModel = addModalState.makeAndModel;
        }

        if (addModalState.makeAndModelId) {
          item.makeAndModelId = addModalState.makeAndModelId;
        }

        // Check if warranty data exists
        if (addModalState.warranty && (addModalState.warranty.provider || addModalState.warranty.terms || addModalState.warranty.termsYears > 0)) {
          item.warranty = {
            provider: addModalState.warranty.provider || '',
            terms: addModalState.warranty.terms || '',
            termsYears: addModalState.warranty.termsYears || 0
          };
        }

        // Check if vendor data exists
        if (addModalState.vendor && addModalState.vendor.name) {
          item.vendor = {
            name: addModalState.vendor.name
          };
        }

        if (addModalState.notes) {
          item.notes = addModalState.notes;
        }

        if (addModalState.documents && addModalState.documents.length > 0) {
          item.attachedDocuments = addModalState.documents;
        }

        itemsToCreate.push(item);
      }

      console.log('Items to create:', itemsToCreate);

      await amsEquipmentService.createBulkEquipment({ projectId, ...(auditId ? { amsProjectAuditId: auditId } : {}), items: itemsToCreate });

      toast.success(`Added ${quantity} ${ghost.assetName}(s)`);
      setAddModalState({ ...addModalState, open: false });

      // Invalidate equipment query cache to force fresh data fetch
      await queryClient.invalidateQueries({ queryKey: ['ams-equipment', projectId] });
      if (onEquipmentUpdate) onEquipmentUpdate();

    } catch (e: any) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to add assets");
    } finally {
      setIsBulkCreatingEquipment(false);
    }
  };

  const handleManualStringGrouping = async () => {
    if (!addModalState.ghost || !addModalState.stringCount) return;
    if (!auditId) { toast.error('Select an audit first'); return; }

    try {
      const { ghost, quantity, stringCount } = addModalState;
      const projectId = project._id || project.id;
      // Canonical kind for the modules being grouped (GAP_REPORT P2-6).
      const moduleAsset = projectAssets.find(pa => (pa._id || (pa as { id?: string }).id) === ghost.projectAssetId);
      const moduleKind = canonicalEquipmentKind(
        ghost.assetName || ghost.assetType,
        (moduleAsset as { moduleType?: string } | undefined)?.moduleType
      );

      // String is NOT a master asset - it's just a grouping container
      // We create strings directly without needing assetId/projectAssetId

      const modulesPerString = Math.floor(quantity / stringCount);
      const remainingModules = quantity % stringCount;

      const createdStrings = [];

      // Create strings as grouping containers
      for (let i = 1; i <= stringCount; i++) {
        // Find existing strings under this parent to get next sequence
        const existingStrings = equipmentList.filter(eq => 
          (eq.parentId === ghost.parentId) && (eq.assetType === 'String')
        );
        let maxStrSeq = 0;
        existingStrings.forEach(s => {
          const match = (s.equipmentName || s.name).match(/-(\d+)$/);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxStrSeq) maxStrSeq = num;
          }
        });

        const strSeq = maxStrSeq + i;
        const strCode = `STR-${strSeq}`;
        const parentEquip = equipmentList.find(e => (e._id || e.id) === ghost.parentId);
        const parentName = parentEquip ? (parentEquip.equipmentName || parentEquip.name) : '';
        const finalStrName = parentName ? `${parentName}-${strCode}` : strCode;

        const stringEquipment = await amsEquipmentService.createEquipment({
          projectId: projectId,
          ...(auditId ? { amsProjectAuditId: auditId } : {}),
          parentId: ghost.parentId,
          assetType: 'String',
          hierarchyLevel: 'String',
          equipmentName: finalStrName,
          status: 'active',
          statusBadge: 'active',
          ...(addModalState.manufacturer && { manufacturerId: addModalState.manufacturer }),
          ...(addModalState.makeAndModelId && { makeAndModelId: addModalState.makeAndModelId }),
        });

        // Calculate how many modules this string should get
        const thisStringModuleCount = i <= remainingModules ? modulesPerString + 1 : modulesPerString;

        createdStrings.push({
          id: stringEquipment._id || stringEquipment.id,
          name: finalStrName,
          moduleCount: thisStringModuleCount
        });
      }

      // Create modules under each string
      for (const string of createdStrings) {
        const siblings = equipmentList.filter(eq =>
          (eq.parentId === string.id) &&
          (eq.assetType === ghost.assetType || canonicalEquipmentKind(eq.assetType) === moduleKind)
        );

        let maxSeq = 0;
        siblings.forEach(s => {
          const match = (s.equipmentName || s.name).match(/-(\d+)$/);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxSeq) maxSeq = num;
          }
        });

        const itemsToCreate = [];
        const masterAsset = masterAssets.find(a =>
          (a._id === ghost.masterAssetId) || (a.id === ghost.masterAssetId)
        );

        let typePrefix: string;
        if (masterAsset?.code) {
          const codeParts = masterAsset.code.split('-');
          typePrefix = codeParts[0].toUpperCase();
        } else {
          typePrefix = ghost.assetName.substring(0, 3).toUpperCase();
        }

        for (let i = 1; i <= string.moduleCount; i++) {
          const sequence = maxSeq + i;
          const code = `${typePrefix}-${sequence}`;
          const equipmentName = `${string.name}-${code}`; // Link modules to their string name

          // Get or create project asset for modules
          let moduleProjectAssetId = ghost.projectAssetId;
          if (!moduleProjectAssetId) {
            const moduleProjectAsset = await amsProjectAssetService.createOrGetAmsProjectAsset(projectId, auditId as string, ghost.masterAssetId);
            moduleProjectAssetId = moduleProjectAsset._id || moduleProjectAsset.id;
          }

          const item: any = {
            equipmentName: equipmentName,
            assetId: moduleProjectAssetId,
            assetType: moduleKind,
            hierarchyLevel: moduleKind,
            parentId: string.id,
            projectId,
            projectAssetId: moduleProjectAssetId,
            status: 'active',
            statusBadge: 'active'
          };

          if (addModalState.manufacturer) item.manufacturerId = addModalState.manufacturer;
          if (addModalState.makeAndModelId) item.makeAndModelId = addModalState.makeAndModelId;
          if (addModalState.warranty && (addModalState.warranty.provider || addModalState.warranty.terms || addModalState.warranty.termsYears > 0)) {
            item.warranty = addModalState.warranty;
          }
          if (addModalState.vendor && addModalState.vendor.name) item.vendor = addModalState.vendor;
          if (addModalState.notes) item.notes = addModalState.notes;
          if (addModalState.documents && addModalState.documents.length > 0) {
            item.attachedDocuments = addModalState.documents;
          }

          itemsToCreate.push(item);
        }

        await amsEquipmentService.createBulkEquipment({ projectId, ...(auditId ? { amsProjectAuditId: auditId } : {}), items: itemsToCreate });
      }

      toast.success(`Created ${stringCount} strings with ${quantity} modules`);

      await queryClient.invalidateQueries({ queryKey: ['ams-equipment', projectId] });
      if (onEquipmentUpdate) onEquipmentUpdate();

      setAddModalState({ ...addModalState, open: false, stringCount: undefined });

    } catch (error: any) {
      console.error('Manual string grouping error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create strings and modules');
    }
  };

  const handleBulkCreateWithStrings = async () => {
    if (!addModalState.ghost || !stringGroupingState.open) return;
    if (!auditId) { toast.error('Select an audit first'); return; }

    setIsBulkCreatingEquipment(true);
    try {
      const { ghost } = addModalState;
      const { parentId, suggestedStrings } = stringGroupingState;
      const projectId = project._id || project.id;
      // Canonical kind for the modules being grouped (GAP_REPORT P2-6).
      const groupedAsset = projectAssets.find(pa => (pa._id || (pa as { id?: string }).id) === ghost.projectAssetId);
      const groupedModuleKind = canonicalEquipmentKind(
        ghost.assetName || ghost.assetType,
        (groupedAsset as { moduleType?: string } | undefined)?.moduleType
      );

      // First, find/create the String asset type
      const stringAsset = masterAssets.find(a => a.name === 'String' || a.name === 'string');
      if (!stringAsset) {
        toast.error('String asset type not found in master assets');
        return;
      }

      const stringProjectAsset = await amsProjectAssetService.createOrGetAmsProjectAsset(
        projectId,
        auditId as string,
        stringAsset._id || stringAsset.id
      );

      const stringProjectAssetId = stringProjectAsset?._id || stringProjectAsset?.id;
      if (!stringProjectAsset || !stringProjectAssetId) {
        toast.error('Failed to create String project asset');
        return;
      }

      const createdStrings = [];

      // Create strings under the inverter
      for (const [index, suggestion] of suggestedStrings.entries()) {
        const parentEquip = equipmentList.find(e => (e._id || e.id) === parentId);
        const parentName = parentEquip ? (parentEquip.equipmentName || parentEquip.name) : '';
        
        // Find existing strings under this parent to get next sequence
        const existingStrings = equipmentList.filter(eq => 
          (eq.parentId === parentId) && (eq.assetType === 'String')
        );
        let maxStrSeq = 0;
        existingStrings.forEach(s => {
          const match = (s.equipmentName || s.name).match(/-(\d+)$/);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxStrSeq) maxStrSeq = num;
          }
        });

        const strSeq = maxStrSeq + index + 1;
        const strCode = `STR-${strSeq}`;
        const finalStrName = parentName ? `${parentName}-${strCode}` : strCode;

        const stringEquipment = await amsEquipmentService.createEquipment({
          projectId: projectId,
          ...(auditId ? { amsProjectAuditId: auditId } : {}),
          assetId: stringProjectAssetId,
          parentId: parentId,
          assetType: 'String',
          hierarchyLevel: 'String',
          equipmentName: finalStrName,
          status: 'active',
          statusBadge: 'active',
          ...(addModalState.manufacturer && { manufacturerId: addModalState.manufacturer }),
          ...(addModalState.makeAndModelId && { makeAndModelId: addModalState.makeAndModelId }),
        });
        createdStrings.push({
          id: stringEquipment._id || stringEquipment.id,
          name: finalStrName,
          moduleCount: suggestion.moduleCount
        });
      }

      // Then, create modules under each string
      for (const string of createdStrings) {
        const siblings = equipmentList.filter(eq =>
          (eq.parentId === string.id) &&
          (eq.assetType === ghost.assetType || canonicalEquipmentKind(eq.assetType) === groupedModuleKind)
        );

        let maxSeq = 0;
        siblings.forEach(s => {
          const match = (s.equipmentName || s.name).match(/-(\d+)$/);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxSeq) maxSeq = num;
          }
        });

        const itemsToCreate = [];
        const masterAsset = masterAssets.find(a =>
          (a._id === ghost.masterAssetId) || (a.id === ghost.masterAssetId)
        );

        let typePrefix: string;
        if (masterAsset?.code) {
          const codeParts = masterAsset.code.split('-');
          typePrefix = codeParts[0].toUpperCase();
        } else {
          typePrefix = ghost.assetName.substring(0, 3).toUpperCase();
        }

        for (let i = 1; i <= string.moduleCount; i++) {
          const sequence = maxSeq + i;
          const code = `${typePrefix}-${sequence}`;
          const equipmentName = `${string.name}-${code}`;

          // Get or create project asset for modules
          let moduleProjectAssetId = ghost.projectAssetId;
          if (!moduleProjectAssetId) {
            const moduleProjectAsset = await amsProjectAssetService.createOrGetAmsProjectAsset(projectId, auditId as string, ghost.masterAssetId);
            moduleProjectAssetId = moduleProjectAsset._id || moduleProjectAsset.id;
          }

          const item: any = {
            equipmentName,
            assetId: moduleProjectAssetId,
            assetType: groupedModuleKind,
            hierarchyLevel: groupedModuleKind,
            parentId: string.id,
            projectId,
            projectAssetId: moduleProjectAssetId,
            status: 'active',
            statusBadge: 'active'
          };

          if (addModalState.manufacturer) {
            item.manufacturerId = addModalState.manufacturer;
          }
          if (addModalState.makeAndModelId) {
            item.makeAndModelId = addModalState.makeAndModelId;
          }
          if (addModalState.warranty && (addModalState.warranty.provider || addModalState.warranty.terms || addModalState.warranty.termsYears > 0)) {
            item.warranty = addModalState.warranty;
          }
          if (addModalState.vendor && addModalState.vendor.name) {
            item.vendor = addModalState.vendor;
          }
          if (addModalState.notes) {
            item.notes = addModalState.notes;
          }
          if (addModalState.documents && addModalState.documents.length > 0) {
            item.attachedDocuments = addModalState.documents;
          }

          itemsToCreate.push(item);
        }

        await amsEquipmentService.createBulkEquipment({ projectId, ...(auditId ? { amsProjectAuditId: auditId } : {}), items: itemsToCreate });
      }

      toast.success(`Created ${suggestedStrings.length} strings with ${stringGroupingState.totalModules} modules`);

      // Invalidate equipment query cache to force fresh data fetch
      await queryClient.invalidateQueries({ queryKey: ['ams-equipment', projectId] });
      if (onEquipmentUpdate) onEquipmentUpdate();

      // Close modals
      setStringGroupingState({ open: false, totalModules: 0, parentType: null, parentId: null, suggestedStrings: [] });
      setAddModalState({ ...addModalState, open: false });

    } catch (error: any) {
      console.error('String grouping error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create strings and modules');
    } finally {
      setIsBulkCreatingEquipment(false);
    }
  };

  // Ensure we have enough width for all nodes plus padding (layoutNodes from useMemo above)
  const maxX = Math.max(...layoutNodes.map(n => n.x + n.width / 2), 1000) + 200;
  const maxY = Math.max(...layoutNodes.map(n => n.y + n.height), 800) + 200;

  const renderNode = (layoutNode: typeof layoutNodes[0]) => {
    const { equipment, x, y, width, height } = layoutNode;

    if (equipment.isGhost) {
      return (
        <motion.div
          key={equipment.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute select-none"
          style={{
            left: `${x - width / 2}px`,
            top: `${y}px`,
            width: `${width}px`
          }}
        >
        {project?.requirement === 'known' && (
          <button
            onClick={() => openAddModal(equipment)}
            className="w-full flex items-center gap-1.5 p-1.5 bg-slate-50 hover:bg-slate-100 transition-all text-left group relative border border-dashed border-slate-300 rounded-lg"
          >
            <Plus size={12} className="text-slate-500 flex-shrink-0" />
            <span className="text-[10px] font-medium text-slate-600 flex-1 break-words italic">
              {equipment.name}
              <span className="ml-0.5 font-semibold text-emerald-600">Recommended</span>
            </span>
          </button>
        )}
        </motion.div>
      );
    }

    const config = getAssetTypeConfig(equipment.assetType, availableAssets);
    const Icon = config.icon;
    const hasChildren = equipment.childEquipmentIds.length > 0;

    const isString = equipment.assetType.toLowerCase().includes('string');
    const moduleChildren = isString
      ? equipment.childEquipmentIds
        .map(id => visualEquipmentList.find(e => e.id === id))
        .filter(e => e && (e.assetType.toLowerCase().includes('module') || e.assetType.toLowerCase().includes('solar')))
      : [];

    const hasModulesInString = moduleChildren.length > 0;

    if (hasModulesInString) {
      const { cols, rows } = calculateGridDimensions(moduleChildren.length);
      const gridHeight = rows * MODULE_HEIGHT + (rows - 1) * MODULE_SPACING;

      return (
        <motion.div
          key={equipment.id}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          className="absolute select-none group"
          style={{
            left: `${x - width / 2}px`,
            top: `${y}px`,
            width: `${width}px`,
            height: `${height}px`
          }}
        >
          <div
            className="relative flex flex-col overflow-hidden transition-shadow duration-200 rounded-lg cursor-pointer hover:shadow-md h-full"
            style={{
              backgroundColor: EQUIPMENT_CARD_BG,
              border: `1px solid ${EQUIPMENT_CARD_BORDER}`,
              padding: '4px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (assignmentMode) {
                toggleEquipmentSelection(equipment.id);
              } else {
                setSelectedNodeId(equipment.id);
                handleEquipmentClick(equipment);
              }
            }}
          >
            {assignmentMode && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -left-2 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEquipmentSelection(equipment.id);
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${selectedEquipmentIds.has(equipment.id)
                    ? 'bg-blue-600 border-blue-600'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                >
                  {selectedEquipmentIds.has(equipment.id) && (
                    <CheckCircle2 size={14} className="text-white" />
                  )}
                </motion.div>
              </motion.div>
            )}

            {getEquipmentResourceRejection(equipment.id).isRejected && !assignmentMode && (
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                className="absolute top-1 right-1 z-10"
              >
                <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600 text-white rounded text-[9px] font-bold">
                  <AlertCircle size={10} />
                  <span className="text-[9px] font-bold">Resource Rejected</span>
                </div>
              </motion.div>
            )}

            {/* Compact header: name + modules count inline */}
            <div className="flex items-center gap-2 shrink-0 py-1.5 px-0.5 border-b border-gray-100/80">
              <div
                className="w-6 h-6 flex items-center justify-center rounded border shrink-0"
                style={{ backgroundColor: `${config.color}18`, borderColor: `${config.color}35` }}
              >
                <Icon size={12} style={{ color: config.color }} />
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-xs text-gray-900 truncate" title={equipment.name}>
                  {equipment.name}
                </h4>
                <span className="text-[10px] text-slate-500 shrink-0 flex items-center gap-0.5">
                  <Grid3x3 size={8} />
                  {moduleChildren.length} Modules
                </span>
                {equipment.isAssigned && equipment.assignedTo?.name && !assignmentMode && !getEquipmentResourceRejection(equipment.id).isRejected && (
                  <span className="text-emerald-600 text-[10px] truncate max-w-[72px]" title={equipment.assignedTo.name}>
                    <UserCheck size={8} className="inline mr-0.5 align-middle" />
                    {equipment.assignedTo.name}
                  </span>
                )}
              </div>
            </div>

            {/* Module grid: solar-panel style — frame, gap background, spaced cells like reference */}
            <div
              className="relative w-full shrink-0 rounded-md border border-slate-300"
              style={{
                height: `${gridHeight + 2 * MODULE_GRID_PADDING}px`,
                backgroundColor: MODULE_GRID_GAP_BG,
                padding: `${MODULE_GRID_PADDING}px`
              }}
            >
              {moduleChildren.map((mod, idx) => {
                if (!mod) return null;
                const row = Math.floor(idx / cols);
                const col = idx % cols;
                const mx = col * (MODULE_WIDTH + MODULE_SPACING);
                const my = row * (MODULE_HEIGHT + MODULE_SPACING);

                return (
                  <motion.div
                    key={mod.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.01 }}
                    className="absolute cursor-pointer rounded-sm overflow-hidden flex flex-col items-center justify-center transition-all hover:brightness-110"
                    style={{
                      left: `${mx}px`,
                      top: `${my}px`,
                      width: `${MODULE_WIDTH}px`,
                      height: `${MODULE_HEIGHT}px`,
                      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 40%, #0F172A 100%)',
                      border: '1px solid #94A3B8',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)'
                    }}
                    title={`${mod.name || `Module ${idx + 1}`} · Solar PV`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (assignmentMode) {
                        toggleEquipmentSelection(mod.id);
                      } else {
                        setSelectedNodeId(mod.id);
                        handleEquipmentClick(mod);
                      }
                    }}
                  >
                    {assignmentMode && (
                      <div className="absolute -top-0.5 -left-0.5 z-20">
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center shadow ${selectedEquipmentIds.has(mod.id)
                            ? 'bg-blue-600 border-blue-400'
                            : 'bg-slate-600/90 border-slate-400'
                            }`}
                        >
                          {selectedEquipmentIds.has(mod.id) && (
                            <Check size={8} className="text-white" />
                          )}
                        </div>
                      </div>
                    )}
                    {/* Busbar line (subtle vertical) */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-px h-full bg-slate-500/30" />
                    </div>
                    <Grid3x3 size={9} className="text-slate-400/80 shrink-0 relative z-10" strokeWidth={1.5} />
                    <span className="text-[7px] font-semibold text-slate-300 tabular-nums leading-tight relative z-10">{idx + 1}</span>
                  </motion.div>
                );
              })}
            </div>

            {/* Compact footer: Delete only — Add child is in equipment details */}
            <div className="flex items-center justify-end gap-0.5 mt-3 pt-2 pb-2.5 border-t border-gray-100/80 shrink-0">
              {!isEquipmentOrDescendantsAssigned(equipment.id) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDeleteEquipment(equipment.id); }}
                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 size={10} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={equipment.id}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="absolute select-none group"
        style={{
          left: `${x - width / 2}px`,
          top: `${y}px`,
          width: `${width}px`
        }}
      >
        <div
          className="relative overflow-hidden transition-shadow duration-200 rounded-lg cursor-pointer hover:shadow-md flex items-stretch min-h-[44px]"
          style={{
            backgroundColor: EQUIPMENT_CARD_BG,
            border: `1px solid ${getEquipmentResourceRejection(equipment.id).isRejected ? '#EF4444' : EQUIPMENT_CARD_BORDER}`,
            boxShadow: getEquipmentResourceRejection(equipment.id).isRejected
              ? '0 2px 6px rgba(239,68,68,0.12)'
              : '0 1px 2px rgba(0,0,0,0.04)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (assignmentMode) {
              toggleEquipmentSelection(equipment.id);
            } else {
              setSelectedNodeId(equipment.id);
              handleEquipmentClick(equipment);
            }
          }}
        >
          {/* Checkbox for assignment mode */}
          {assignmentMode && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -left-2 z-20"
              onClick={(e) => {
                e.stopPropagation();
                toggleEquipmentSelection(equipment.id);
              }}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all shadow ${selectedEquipmentIds.has(equipment.id)
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
              >
                {selectedEquipmentIds.has(equipment.id) && (
                  <CheckCircle2 size={14} className="text-white" />
                )}
              </motion.div>
            </motion.div>
          )}

          {/* Resource Rejection Badge */}
          {getEquipmentResourceRejection(equipment.id).isRejected && !assignmentMode && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 z-20"
            >
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600 text-white rounded text-[9px] font-semibold">
                <AlertCircle size={8} />
                <span>Rejected</span>
              </div>
            </motion.div>
          )}

          {/* Compact single row: icon | name + meta | expand (parent) | actions — extra top/bottom padding so name is visible */}
          <div className="flex items-center gap-2 py-3 px-3 flex-1 min-w-0 relative z-10">
            <div
              className="w-7 h-7 flex items-center justify-center rounded border shrink-0"
              style={{ backgroundColor: `${config.color}18`, borderColor: `${config.color}35` }}
            >
              <Icon size={12} style={{ color: config.color }} />
            </div>
            <div className="flex-1 min-w-[72px] overflow-hidden">
              <h4 className="font-semibold text-xs text-gray-900 truncate leading-tight block" title={equipment.name}>
                {equipment.name}
              </h4>
              <p className="text-[10px] text-gray-500 flex items-center gap-1.5 flex-wrap gap-y-0 overflow-hidden min-w-0">
                <span className="truncate max-w-[70px] shrink-0" title={equipment.assetType}>{equipment.assetType}</span>
                {hasChildren && (
                  <>
                    <span className="text-gray-300 shrink-0">·</span>
                    <span className="text-slate-600 shrink-0 font-medium whitespace-nowrap">{equipment.childEquipmentIds.length} child{equipment.childEquipmentIds.length !== 1 ? 'ren' : ''}</span>
                  </>
                )}
                {equipment.isAssigned && equipment.assignedTo?.name && !assignmentMode && !getEquipmentResourceRejection(equipment.id).isRejected && (
                  <>
                    <span className="text-gray-300 shrink-0">·</span>
                    <span className="text-emerald-600 truncate max-w-[60px] shrink min-w-0" title={equipment.assignedTo.name}>
                      <UserCheck size={8} className="inline shrink-0 mr-0.5 align-middle" />
                      {equipment.assignedTo.name}
                    </span>
                  </>
                )}
              </p>
            </div>
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedNodes(prev => {
                    const next = new Set(prev);
                    if (next.has(equipment.id)) next.delete(equipment.id);
                    else next.add(equipment.id);
                    return next;
                  });
                }}
                className={`p-1 rounded shrink-0 transition-colors ${expandedNodes.has(equipment.id)
                  ? 'bg-blue-100 text-blue-600 ring-1 ring-blue-200'
                  : 'bg-slate-200/80 text-slate-500 hover:bg-slate-300'
                  }`}
                title={expandedNodes.has(equipment.id) ? 'Collapse' : 'Expand'}
              >
                {expandedNodes.has(equipment.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            )}
            {!isEquipmentOrDescendantsAssigned(equipment.id) && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDeleteEquipment(equipment.id); }}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                title="Delete"
              >
                <Trash2 size={10} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
    
      <div className="h-full flex flex-col bg-white relative z-0">
        <style>{`
          .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
          /* Premium button typography - industry standard */
          .btn-premium { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif; font-weight: 600; letter-spacing: -0.01em; }
        `}</style>

        {/* Single Panel: Assets above, Equipment below (no tabs) */}
        <div className="flex-1 flex flex-col">
          <div className="sticky top-0 z-10 border-b border-gray-200/90 bg-white/95 backdrop-blur-sm shadow-sm">
            <div className="flex items-center justify-between py-2.5 px-3 gap-3 flex-wrap">
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-600 border border-blue-700/30">
                  <Box size={14} className="text-white" />
                </div>
                <h2 className="font-semibold text-sm text-gray-800 tracking-tight">Asset</h2>
                <span className="text-[9px] px-1.5 py-px rounded-full font-semibold tabular-nums bg-blue-600 text-white">
                  {projectAssets?.length || 0} assets
                </span>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setShowProjectAssets(!showProjectAssets)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border border-gray-200"
                  title={showProjectAssets ? 'Hide Assets & Equipment' : 'Show Assets & Equipment'}
                >
                  <ChevronDown 
                    size={14} 
                    className={`transition-transform duration-200 ${showProjectAssets ? 'rotate-0' : '-rotate-90'}`}
                  />
                  <span className="text-[11px] font-medium">{showProjectAssets ? 'Hide' : 'Show'}</span>
                </button>
                {onAddMasterAsset && project?.requirement === 'known' && showProjectAssets && (
                  <button
                    onClick={onAddMasterAsset}
                    className="btn-premium flex items-center gap-1.5 px-3 py-2 text-[13px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98]"
                  >
                    <Plus size={14} />
                    <span>Add Asset</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Assets section (above equipment) - relative z-[100] so dropdown shows above equipment tree */}
          {showProjectAssets && (
            <div className={`flex-shrink-0 overflow-auto custom-scrollbar border-b border-gray-200/80 relative z-[100] ${assetsSectionRejected ? 'bg-red-50/60 ring-1 ring-red-200' : 'bg-slate-50'}`}>
              {/* Banner when Assets & Equipment rejected at project level */}
              {assetsSectionRejected && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-100 border-b border-red-200 text-red-800 text-xs font-medium">
                  <XCircle size={14} className="flex-shrink-0" />
                  <span>Assets &amp; Equipment rejected. Update as needed and resubmit for approval.</span>
                </div>
              )}
              {/* Project Assets Header */}
              <div className="flex items-center justify-between p-3 border-b border-gray-200/50">
                <div className="flex items-center gap-1.5">
                  <CheckCircle size={14} className="text-blue-600" />
                  <h3 className="font-semibold text-xs text-gray-700">Project Assets</h3>
                  <span className="bg-blue-100 text-blue-700 text-[9px] px-1.5 py-px rounded-full font-semibold tabular-nums">
                    {projectAssets?.length || 0}
                  </span>
                </div>
                {selectedAuditId && (
                  <button
                    onClick={handleOpenProjectCheckpoints}
                    className={`btn-premium flex items-center gap-1 px-2 py-1 border rounded-lg text-[10px] font-semibold transition-all duration-200 ${
                      rejectedProjectCheckpointCount > 0
                        ? 'bg-red-50 hover:bg-red-100 border-red-300 text-red-700'
                        : 'bg-white hover:bg-blue-50 border-blue-200 hover:border-blue-300 text-blue-600'
                    }`}
                    title={rejectedProjectCheckpointCount > 0 ? 'Has rejected project-level checkpoints — update and resubmit' : 'View checkpoints that apply to the whole project (not tied to a specific asset)'}
                  >
                    <ListChecks size={10} />
                    Project Checkpoints
                    <span className="tabular-nums opacity-80">({projectActiveCheckpointCount})</span>
                    {rejectedProjectCheckpointCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 ml-0.5 px-1 py-px rounded text-[9px] font-medium bg-red-100 text-red-700 border border-red-200">
                        <XCircle size={8} /> {rejectedProjectCheckpointCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
              <div className="p-3">
                  {projectAssets && projectAssets.length > 0 && (
                    <div className="flex flex-wrap gap-3">
                      {projectAssets.map((asset, index) => {
                    const config = getAssetTypeConfig(asset.name, availableAssets);
                    const Icon = config.icon;
                    const assetId = asset._id || (asset as any).id;
                    const assetIdStr = assetId != null ? String(assetId) : '';
                    const equipmentCount = equipmentList.filter(eq => {
                      const eqProjectAssetId = (eq.projectAssetId as any)?._id || eq.projectAssetId || (eq.projectAssetId as any)?.toString();
                      const eqAssetId = (eq.assetId as any)?._id || eq.assetId || (eq.assetId as any)?.toString();
                      return eqProjectAssetId === assetIdStr || eqAssetId === assetIdStr;
                    }).length;
                    const rejectionInfo = getAssetRejectionInfo(asset._id);
                    const hasRejection = rejectionInfo.hasRejections || amsRejectedAssetIds.has(assetIdStr);
                    const assetUpdated = selectedAuditId && hasRejection && updatedAssetIds.has(`${selectedAuditId}:${assetIdStr}`);
                    const isSelectedForCheckpoints = !!(
                      showAssetCheckpointsDrawer &&
                      selectedAssetForAuditCheckpoints &&
                      (selectedAssetForAuditCheckpoints._id || selectedAssetForAuditCheckpoints.id) === assetIdStr
                    );
                    return (
                      <motion.div
                        key={assetIdStr || index}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="flex-shrink-0 w-[11rem]"
                      >
                        <div
                          className={`bg-white rounded-lg border shadow-sm hover:shadow transition-all overflow-hidden cursor-pointer ${assetUpdated
                            ? 'border-amber-300 ring-1 ring-amber-100'
                            : hasRejection
                            ? 'border-red-300 ring-1 ring-red-100'
                            : rejectionInfo.hasUpdated
                              ? 'border-amber-300 ring-1 ring-amber-100'
                              : isSelectedForCheckpoints
                                ? 'border-blue-300 ring-1 ring-blue-100 bg-blue-50/40 shadow'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          title={assetUpdated ? 'Updated — resubmit for approval' : hasRejection ? 'Has rejected checkpoints — update and resubmit' : isSelectedForCheckpoints ? 'Viewing checkpoints for this asset' : undefined}
                        >
                          <div className="p-2">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <div
                                className={`w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0 ${assetUpdated ? 'ring-1 ring-amber-400' : hasRejection ? 'ring-1 ring-red-400' : rejectionInfo.hasUpdated ? 'ring-1 ring-amber-400' : ''}`}
                                style={{
                                  background: assetUpdated
                                    ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)'
                                    : hasRejection
                                    ? 'linear-gradient(135deg, #FEE2E2, #FCA5A5)'
                                    : rejectionInfo.hasUpdated
                                      ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)'
                                      : `linear-gradient(135deg, ${config.color}15, ${config.color}28)`,
                                  borderColor: assetUpdated ? '#D97706' : hasRejection ? '#EF4444' : rejectionInfo.hasUpdated ? '#D97706' : config.color + '40',
                                  borderWidth: '1px'
                                }}
                              >
                                <Icon size={14} style={{ color: assetUpdated ? '#B45309' : hasRejection ? '#DC2626' : rejectionInfo.hasUpdated ? '#B45309' : config.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1 flex-wrap">
                                  <h4 className="font-semibold text-[11px] text-gray-900 truncate">{asset.name}</h4>
                                  {assetUpdated && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[9px] font-medium bg-amber-100 text-amber-800 border border-amber-200 shrink-0" title="Updated — resubmit">
                                      Updated
                                    </span>
                                  )}
                                  {hasRejection && !assetUpdated && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[9px] font-medium bg-red-100 text-red-700 border border-red-200 shrink-0" title="Has rejected checkpoints">
                                      <XCircle size={8} /> Rejected
                                    </span>
                                  )}
                                </div>
                                <p className="text-[9px] text-gray-500 tabular-nums">{equipmentCount} eq</p>
                              </div>
                            </div>
                            <div className="flex gap-1 mt-2">
                              {selectedAuditId && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenAssetCheckpoints(asset);
                                  }}
                                  className="btn-premium flex items-center justify-center gap-1 px-2 py-1.5 border rounded-lg text-[10px] font-semibold bg-white hover:bg-blue-50 border-blue-200 hover:border-blue-300 text-blue-600 transition-all duration-200 flex-1"
                                  title={`${assetActiveCheckpointCounts.get(assetIdStr) || 0} active checkpoint${(assetActiveCheckpointCounts.get(assetIdStr) || 0) === 1 ? '' : 's'}`}
                                >
                                  <ListChecks size={10} />
                                  Checkpoints
                                  <span className="tabular-nums opacity-80">({assetActiveCheckpointCounts.get(assetIdStr) || 0})</span>
                                </button>
                              )}
                              {equipmentCount === 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveProjectAsset(asset);
                                  }}
                                  className="btn-premium flex items-center justify-center px-2 py-1.5 border rounded-lg text-[11px] font-semibold bg-white hover:bg-red-50 border-slate-200 hover:border-red-300 text-red-600 transition-all duration-200"
                                  title="Remove asset"
                                >
                                  <Trash2 size={10} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                      );
                    })}
                    </div>
                  )}

                  {/* Add bulk equipment for these assets - only when project has assets and requirement is known */}
                  {projectAssets && projectAssets.length > 0 && project?.requirement === 'known' && (
                    <div className="mt-3 pt-3 border-t border-gray-200/80 flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                          Add equipment for these assets
                        </p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="flex-shrink-0">
                            <button
                              onClick={() => {
                                setChildSelectionState({
                                  open: true,
                                  parentId: null,
                                  parentType: null,
                                  warningOpen: false,
                                  selectedAsset: null
                                });
                              }}
                              disabled={isBulkCreatingEquipment}
                              className={`btn-premium flex items-center gap-1.5 px-3 py-2 text-[13px] rounded-xl font-semibold transition-all duration-200 ease-out active:scale-[0.98] ${
                                isBulkCreatingEquipment
                                  ? 'bg-slate-400 cursor-not-allowed text-white shadow-none'
                                  : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md hover:shadow-lg'
                              }`}
                            >
                              {isBulkCreatingEquipment ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Plus size={14} />
                              )}
                              <span>{isBulkCreatingEquipment ? 'Adding…' : 'Add Bulk Equipment'}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      {visualEquipmentList.length > 0 && (
                        <span className="text-[10px] font-semibold text-gray-500 tabular-nums">
                          {visualEquipmentList.length} equipment
                        </span>
                      )}
                    </div>
                  )}

                  {(!projectAssets || projectAssets.length === 0) && (
                    <div className="text-center py-8 bg-white rounded-xl border-2 border-dashed border-gray-200">
                      {project?.requirement === 'unknown' ? (
                        <div className="space-y-3">
                          <p className="text-[11px] text-gray-500 mb-2">Asset & Equipment selection is not available</p>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                            <p className="text-xs text-amber-700">
                              Asset and equipment selection isn’t available for this project yet.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-[11px] text-gray-500 mb-2">No assets configured</p>
                          {onAddMasterAsset && (
                            <button
                              onClick={onAddMasterAsset}
                              className="btn-premium px-4 py-2 text-[13px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98]"
                            >
                              <Plus size={14} /> Add Asset
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Equipment section (below assets) */}
          {showProjectAssets && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-auto relative custom-scrollbar bg-slate-50">
            {/* Loading State - Data is being fetched */}
            {equipmentLoading && (
              <TreeLoadingState message="Loading equipment hierarchy..." />
            )}

            {/* Error State - Failed to fetch equipment */}
            {equipmentError && !equipmentLoading && (
              <TreeErrorState
                error={equipmentErrorDetails}
                onRetry={() => refetchEquipment()}
              />
            )}

            {/* Empty State - No Equipment (only show when not loading and no error) */}
            {!equipmentLoading && !equipmentError && visualEquipmentList.length === 0 && (
              <div className="flex items-center justify-center h-full p-8">
                <div className="max-w-2xl w-full">
                  {projectAssets && projectAssets.length > 0 ? (
                    project?.requirement === 'unknown' ? (
                      /* Unknown requirement - show message */
                      <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-amber-200">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
                            <AlertCircle size={32} className="text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            Asset & Equipment Selection Not Available
                          </h3>
                          <p className="text-gray-600 text-sm mb-4">
                            Asset and equipment selection isn’t available for this project yet.
                          </p>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                            <p className="text-xs text-amber-700">
                              Update the project settings to enable asset and equipment management.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Has project assets - show option to add equipment */
                      <>
                        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
                          <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
                              <Network size={32} className="text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                              Ready to Build Your Equipment Hierarchy
                            </h3>
                            <p className="text-gray-600 text-sm">
                              You have {projectAssets.length} asset{projectAssets.length > 1 ? 's' : ''} configured.
                              Choose an asset type below to create your first equipment.
                            </p>
                          </div>

                          <div className="space-y-3 mb-6">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              Available Project Assets
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {projectAssets.map((asset) => {
                                const config = getAssetTypeConfig(asset.name, availableAssets);
                                const Icon = config.icon;

                                return (
                                  <button
                                    key={asset._id || (asset as any).id}
                                    onClick={() => {
                                      console.log('Asset clicked:', asset);
                                      // Open modal to add equipment of this type
                                      setAddModalState({
                                        open: true,
                                        ghost: {
                                          masterAssetId: asset.masterAssetId,
                                          projectAssetId: asset._id,  // Add projectAssetId here
                                          assetName: asset.name,
                                          assetType: asset.name,
                                          parentId: null,
                                          config: {
                                            unitsPerParent: 1,
                                            namingPattern: '{asset}-{seq}'
                                          }
                                        },
                                        quantity: 1,
                                        namingPattern: '{asset}-{seq}',
                                        documents: []
                                      });
                                    }}
                                    className="btn-premium flex items-center gap-3 p-4 bg-gradient-to-br from-white to-slate-50/80 border border-slate-200 rounded-xl hover:border-emerald-400 hover:shadow-lg transition-all duration-200 ease-out group text-left text-slate-800"
                                  >
                                    <div
                                      className="w-12 h-12 flex items-center justify-center rounded-xl shadow-md group-hover:shadow-lg transition-all flex-shrink-0 group-hover:scale-110"
                                      style={{
                                        background: `linear-gradient(135deg, ${config.color}15, ${config.color}30)`,
                                        borderColor: config.color + '40',
                                        borderWidth: '2px'
                                      }}
                                    >
                                      <Icon size={20} style={{ color: config.color }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-sm text-gray-900 group-hover:text-green-600 transition-colors truncate">
                                        {asset.name}
                                      </h4>
                                      <p className="text-xs text-gray-500">
                                        Click to add equipment
                                      </p>
                                    </div>
                                    <Plus size={20} className="text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <AlertCircle size={16} className="text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-green-900 mb-1 text-sm">Getting Started</h4>
                              <p className="text-xs text-green-800">
                                Click on any asset type above to create your first equipment.
                                You can build a hierarchy by adding child equipment to parent equipment.
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  ) : (
                    /* No project assets - prompt to add assets in Asset tab first */
                    <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
                        <Network size={32} className="text-gray-400" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        No Equipment Yet
                      </h3>
                      <p className="text-gray-600 text-sm mb-6">
                        To create equipment, add project assets first in the Asset tab.
                      </p>
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <AlertCircle size={16} className="text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <h4 className="font-semibold text-amber-900 mb-1 text-sm">Next Step</h4>
                            <p className="text-xs text-amber-800">
                              Add project assets above, then return here to build your equipment hierarchy.
                            </p>
                          </div>
                        </div>
                      </div>
                      {onAddMasterAsset && project?.requirement === 'known' && (
                        <button
                          onClick={onAddMasterAsset}
                          className="btn-premium inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98]"
                        >
                          <Box size={18} />
                          Add Asset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assignment Mode Toolbar */}
            {assignmentMode && visualEquipmentList.length > 0 && (
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        setAssignmentMode(false);
                        clearSelection();
                      }}
                      className="btn-premium flex items-center gap-2 px-3 py-2 text-[13px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all duration-200"
                    >
                      <X size={16} />
                      Cancel
                    </button>

                    <div className="h-6 w-px bg-gray-300" />

                    <span className="text-sm font-medium text-gray-700">
                      {selectedEquipmentIds.size} equipment selected
                    </span>

                    {selectedEquipmentIds.size > 0 && (
                      <>
                        <button
                          onClick={selectAllEquipment}
                          className="btn-premium text-[13px] text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                        >
                          Select All
                        </button>
                        <button
                          onClick={clearSelection}
                          className="btn-premium text-[13px] text-slate-600 hover:text-slate-700 font-semibold transition-colors"
                        >
                          Clear
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => setShowAssignmentDrawer(true)}
                    disabled={selectedEquipmentIds.size === 0}
                    className="btn-premium flex items-center gap-2 px-4 py-2.5 text-[13px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:from-slate-400 disabled:to-slate-400 disabled:cursor-not-allowed text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98]"
                  >
                    <UserCheck size={16} />
                    Assign to Resource ({selectedEquipmentIds.size})
                  </button>
                </div>
              </div>
            )}

            {/* Tree View with Zoom - z-index 0 so Add Equipment / drawers (portaled, z-[99999]) stay on top */}
            {visualEquipmentList.length > 0 && (
              <div
                ref={scrollContainerRef}
                className="relative overflow-x-auto overflow-y-auto custom-scrollbar"
                style={{
                  width: `${Math.max(maxX + 200, 100)}px`,
                  height: `${Math.max(maxY + 100, 100)}px`,
                  minWidth: '100%',
                  minHeight: '100%',
                  zIndex: 0
                }}
              >
                <div
                  ref={treeContainerRef}
                  className="relative origin-top-left transition-transform duration-300"
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: '0 0',
                    width: `${Math.max(maxX + 200, 100)}px`,
                    height: `${Math.max(maxY + 100, 100)}px`,
                    padding: '20px'
                  }}
                >
                  {/* Silent grid background for equipment tree (20px inset) */}
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: TREE_GRID.PADDING,
                      top: TREE_GRID.PADDING,
                      right: TREE_GRID.PADDING,
                      bottom: TREE_GRID.PADDING,
                      backgroundImage: `
                        linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)
                      `,
                      backgroundSize: `${TREE_GRID.STEP}px ${TREE_GRID.STEP}px`,
                      zIndex: 0
                    }}
                  />

                  <TreeConnections layoutNodes={layoutNodes} expandedNodes={expandedNodes} />
                  <TreeGroupBorders
                    layoutNodes={layoutNodes}
                    expandedNodes={expandedNodes}
                    visualEquipmentList={visualEquipmentList}
                  />

                  <div style={{ zIndex: 2 }}>
                    {layoutNodes.map(renderNode)}
                  </div>

                </div>
              </div>
            )}

              </div>
                  {/* Add Bulk Equipment - Right Side Drawer (choose equipment, then configure bulk add) - Only show if requirement is known */}
              {project?.requirement === 'known' && (
                <BulkEquipmentDrawer
                open={addModalState.open}
                onClose={() => setAddModalState({ ...addModalState, open: false })}
              title={addModalState.ghost ? `Add Bulk Equipment - ${addModalState.ghost.assetName}` : 'Add Bulk Equipment'}
              addModalState={addModalState}
              setAddModalState={setAddModalState}
              isBulkCreatingEquipment={isBulkCreatingEquipment}
              onBulkCreate={handleBulkCreate}
              manufacturers={manufacturers}
              dropdownMakeAndModels={dropdownMakeAndModels}
              newBulkDocument={newBulkDocument}
              setNewBulkDocument={setNewBulkDocument}
              onAddBulkDocument={handleAddBulkDocument}
              onRemoveBulkDocument={handleRemoveBulkDocument}
              onOpenManufacturerCreator={() => setShowManufacturerCreator(true)}
              onOpenMakeAndModelCreator={() => {
                setSelectedManufacturerForModel(addModalState.manufacturer ?? '');
                setShowMakeAndModelCreator(true);
              }}
              equipmentList={equipmentList}
              projectAssets={projectAssets}
              visualEquipmentList={visualEquipmentList}
              availableAssets={availableAssets}
                  getAssetTypeConfig={getAssetTypeConfig}
                />
              )}

              {/* Child Asset Selection - Right Drawer (Add Child Equipment) - Only show if requirement is known */}
              {project?.requirement === 'known' && (
                <RightDrawer
                open={childSelectionState.open}
                onClose={() => setChildSelectionState({ ...childSelectionState, open: false })}
                title={childSelectionState.parentId ? "Add Child Equipment" : "Select Asset to Add"}
                size="md"
              >
              <div className="space-y-4">
                {childSelectionState.parentId && (
                  <p className="text-sm text-gray-500">
                    Parent: {visualEquipmentList.find(e => e.id === childSelectionState.parentId)?.name}
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  {childSelectionState.parentId ? "Select an asset type to add:" : "Select the asset type for bulk creation:"}
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {projectAssets.map(asset => {
                    const config = getAssetTypeConfig(asset.name, availableAssets);
                    const Icon = config.icon;

                    const recommended = getRecommendedChildren(childSelectionState.parentType || '');
                    const isRecommended = recommended.some(rec => {
                      const recType = rec.assetType.toLowerCase();
                      const assetName = asset.name.toLowerCase();
                      return recType === assetName ||
                        (recType.includes('module') && assetName.includes('module')) ||
                        (recType.includes('string') && assetName.includes('string')) ||
                        (recType.includes('inverter') && assetName.includes('inverter')) ||
                        (recType.includes('transformer') && assetName.includes('transformer')) ||
                        assetName.includes(recType) ||
                        recType.includes(assetName);
                    });

                    return (
                      <button
                        key={asset._id || (asset as any).id}
                        onClick={() => {
                          if (isRecommended) {
                            const projectAsset = projectAssets.find(pa => pa.masterAssetId === asset.masterAssetId);
                            const matchingRec = recommended.find(rec => {
                              const recType = rec.assetType.toLowerCase();
                              const assetName = asset.name.toLowerCase();
                              return recType === assetName ||
                                (recType.includes('module') && assetName.includes('module')) ||
                                (recType.includes('string') && assetName.includes('string')) ||
                                (recType.includes('inverter') && assetName.includes('inverter')) ||
                                (recType.includes('transformer') && assetName.includes('transformer')) ||
                                assetName.includes(recType) ||
                                recType.includes(assetName);
                            });
                            const rec = matchingRec || (recommended[0] as StandardChildConfig) || { suggestedQuantity: 1, unitsPerParent: 1, namingPattern: '{asset}-{seq}' };

                            setAddModalState({
                              open: true,
                              ghost: {
                                masterAssetId: asset.masterAssetId,
                                projectAssetId: projectAsset?._id,
                                assetName: asset.name,
                                assetType: asset.name,
                                parentId: childSelectionState.parentId,
                                parentType: childSelectionState.parentType ?? undefined,
                                config: {
                                  unitsPerParent: rec.unitsPerParent || 1,
                                  namingPattern: rec.namingPattern || '{asset}-{seq}'
                                }
                              },
                              quantity: rec.suggestedQuantity || 1,
                              namingPattern: rec.namingPattern || '{asset}-{seq}',
                              documents: []
                            });
                            setChildSelectionState({ ...childSelectionState, open: false });
                          } else {
                            const projectAsset = projectAssets.find(pa => pa.masterAssetId === asset.masterAssetId);
                            setAddModalState({
                              open: true,
                              ghost: {
                                masterAssetId: asset.masterAssetId,
                                projectAssetId: projectAsset?._id,
                                assetName: asset.name,
                                assetType: asset.name,
                                parentId: childSelectionState.parentId,
                                parentType: childSelectionState.parentType ?? undefined,
                                config: {
                                  unitsPerParent: 1,
                                  namingPattern: '{asset}-{seq}'
                                }
                              },
                              quantity: 1,
                              namingPattern: '{asset}-{seq}',
                              documents: []
                            });
                            setChildSelectionState({ ...childSelectionState, open: false, selectedAsset: null });
                          }
                        }}
                        className={`btn-premium flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 ease-out ${isRecommended
                          ? 'border-emerald-200 bg-emerald-50/80 hover:border-emerald-300 hover:shadow-md'
                          : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/80'
                          }`}
                      >
                        <div
                          className="w-10 h-10 flex items-center justify-center rounded-xl"
                          style={{
                            background: isRecommended ? '#dcfce7' : `linear-gradient(135deg, ${config.color}15, ${config.color}30)`,
                            color: isRecommended ? '#166534' : config.color
                          }}
                        >
                          <Icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{asset.name}</h4>
                          {isRecommended && (
                            <span className="inline-block px-1.5 py-0.5 bg-green-200 text-green-800 text-[10px] rounded font-bold mt-1">
                              Recommended
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  </div>
                </div>
              </RightDrawer>
              )}

              {/* Equipment Details Drawer */}

              {/* Equipment Assignment Drawer */}
              <EquipmentAssignmentDrawer
              isOpen={showAssignmentDrawer}
              onClose={() => setShowAssignmentDrawer(false)}
              projectId={project._id || project.id}
              selectedEquipmentIds={Array.from(selectedEquipmentIds)}
              equipmentList={equipmentList}
              onAssignmentComplete={() => {
                clearSelection();
                setAssignmentMode(false);
                fetchEquipment(); // Refresh to show updated assignments
                fetchAssignees(); // Refresh to get latest assignee names
                  if (onEquipmentUpdate) onEquipmentUpdate();
                }}
              />

              {/* Asset Checkpoints Drawer */}
              <ProjectAssetCheckpointDrawer
              open={showCheckpointsDrawer}
              onClose={() => {
                setShowCheckpointsDrawer(false);
                setSelectedAssetForCheckpoints(null);
              }}
              projectAsset={selectedAssetForCheckpoints}
              onRefresh={() => {
                // Notify parent so it refetches audits/approvals and the asset list,
                // which refreshes the AMS rejected/updated badges (sourced from the
                // audit approval, not the OMS project-approval endpoint).
                if (onEquipmentUpdate) onEquipmentUpdate();
              }}
              />

              {/* Equipment Edit Modals */}

              <EquipmentEditDrawer
              open={showEquipmentEditDrawer}
              onClose={() => {
                setShowEquipmentEditDrawer(false);
                setCurrentEquipment(null);
              }}
              equipment={currentEquipment}
              manufacturers={manufacturers}
              makeAndModels={makeAndModels}
              onSuccess={handleEquipmentEditSuccess}
              onAddChild={project?.requirement === 'known' ? (eq) => {
                setShowEquipmentEditDrawer(false);
                setCurrentEquipment(null);
                setChildSelectionState({
                  open: true,
                  parentId: eq._id || eq.id,
                  parentType: eq.assetType || eq.hierarchyLevel || '',
                  warningOpen: false,
                  selectedAsset: null
                  });
                } : undefined}
              />
            </div>
          )}
        </div>

      {/* Zoom Controls - when equipment tree is visible */}
      {visualEquipmentList.length > 0 && (
        <ZoomControls
          zoomLevel={zoomLevel}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onReset={resetZoom}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
        />
      )}

      {/* Confirmation Drawer */}
      <ConfirmationDrawer
        open={confirmationState.open}
        onClose={() => setConfirmationState(prev => ({ ...prev, open: false }))}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        variant={confirmationState.variant}
        loading={confirmationState.loading}
      />

      {/* String Grouping Suggestion Modal */}
      <RightDrawer
        open={stringGroupingState.open}
        onClose={() => {
          setStringGroupingState({ ...stringGroupingState, open: false });
        }}
        title="Suggested String Grouping"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Recommended Organization</h4>
            <p className="text-sm text-blue-700">
              You're adding {stringGroupingState.totalModules} Solar Modules under a {stringGroupingState.parentType}.
              We recommend organizing them into strings for better management.
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Suggested Distribution</label>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              {stringGroupingState.suggestedStrings.map((str, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-gray-200">
                  <span className="font-medium text-gray-900">{str.stringName}</span>
                  <span className="text-sm text-gray-600">{str.moduleCount} modules</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => {
                // User declined, create modules directly without strings
                setStringGroupingState({ ...stringGroupingState, open: false });
                // Don't proceed with bulk create, user needs to click Add Assets again
              }}
              className="btn-premium flex-1 px-4 py-2.5 text-[13px] font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
            >
              Skip (No Strings)
            </button>
            <button
              onClick={handleBulkCreateWithStrings}
              disabled={isBulkCreatingEquipment}
              className={`btn-premium flex-1 px-4 py-2.5 text-[13px] font-semibold text-white rounded-xl flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98] ${
                isBulkCreatingEquipment
                  ? 'bg-slate-400 cursor-not-allowed shadow-none'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
              }`}
            >
              {isBulkCreatingEquipment && <Loader2 size={16} className="animate-spin" />}
              {isBulkCreatingEquipment ? 'Creating...' : 'Create with Strings'}
            </button>
          </div>
        </div>
      </RightDrawer>

      {/* Manufacturer Creator Drawer */}
      <RightDrawer
        open={showManufacturerCreator}
        onClose={() => setShowManufacturerCreator(false)}
        title="Create New Manufacturer"
        size="lg"
      >
        <ManufacturerForm
          editingItem={null}
          onAdd={handleManufacturerCreated}
          onClose={() => setShowManufacturerCreator(false)}
        />
      </RightDrawer>

      {/* Make & Model Creator Drawer */}
      <RightDrawer
        open={showMakeAndModelCreator}
        onClose={() => setShowMakeAndModelCreator(false)}
        title="Create New Make & Model"
        size="lg"
      >
        <MakeAndModelForm
          editingItem={null}
          manufacturers={manufacturers}
          onAdd={handleMakeAndModelCreated}
          onClose={() => setShowMakeAndModelCreator(false)}
          onManufacturerAdded={async (newMfg: any) => {
            // Refresh manufacturers list when new manufacturer is created from within MakeAndModelForm
            const mfgData = await manufacturerService.manufacturerService.getManufacturers({ limit: 1000 });
            setManufacturers(mfgData.data || []);
          }}
        />
      </RightDrawer>
      {/* Asset Checkpoints Drawer */}
      {selectedAuditId && selectedAudit && selectedAssetForAuditCheckpoints && (
        <RightDrawer
          open={showAssetCheckpointsDrawer}
          onClose={() => {
            setShowAssetCheckpointsDrawer(false);
            setSelectedAssetForAuditCheckpoints(null);
            setAssetCheckpoints([]);
            setAvailableAssetCheckpoints([]);
            setExpandedAssetCheckpoint(null);
            setEditingAssetCheckpoint(null);
            setShowCreateAssetCheckpoint(false);
            resetCheckpointFilters();
          }}
          title={`Asset Checkpoints - ${selectedAssetForAuditCheckpoints.name} (${getSelectedAuditTypeLabel(selectedAudit)})`}
          size="3xl"
        >
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Building2 size={20} className="text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-emerald-900">Asset Checkpoints</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    View and manage checkpoints for <strong>{selectedAssetForAuditCheckpoints.name}</strong> in this audit.
                  </p>
                </div>
              </div>
            </div>

            {/* Create Custom Checkpoint Button */}
            {!showCreateAssetCheckpoint && (
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCreateAssetCheckpoint(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  <Plus size={16} />
                  Create Custom Checkpoint
                </button>
              </div>
            )}

            {/* Create Form Card */}
            {showCreateAssetCheckpoint && selectedAudit && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Create Custom Asset Checkpoint</h4>
                  <button
                    onClick={() => setShowCreateAssetCheckpoint(false)}
                    className="p-1.5 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100"
                  >
                    <X size={18} />
                  </button>
                </div>
                <AmsAuditCheckpointForm
                  amsProjectAuditId={selectedAuditId}
                  checkpointType="asset"
                  amsProjectAssetId={selectedAssetForAuditCheckpoints?._id || selectedAssetForAuditCheckpoints?.id || null}
                  auditTypeId={getSelectedAuditTypeId(selectedAudit)}
                  projectAssets={projectAssets.map(asset => ({
                    _id: asset._id || asset.id || '',
                    name: asset.name || 'Unknown Asset',
                  }))}
                  onSuccess={() => {
                    setShowCreateAssetCheckpoint(false);
                    if (selectedAssetForAuditCheckpoints) {
                      fetchAssetCheckpoints(selectedAssetForAuditCheckpoints._id || selectedAssetForAuditCheckpoints.id || '', extractMasterAssetId(selectedAssetForAuditCheckpoints));
                    }
                    fetchActiveCheckpointCounts();
                    onCheckpointsUpdated?.();
                  }}
                  onCancel={() => setShowCreateAssetCheckpoint(false)}
                  mode="create"
                />
              </div>
            )}

            {!loadingAssetCheckpoints && assetCheckpoints.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={checkpointSearchQuery}
                    onChange={(e) => setCheckpointSearchQuery(e.target.value)}
                    placeholder="Search checkpoints by name..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  />
                </div>
                <select
                  value={checkpointExecutionTypeFilter}
                  onChange={(e) => setCheckpointExecutionTypeFilter(e.target.value as typeof checkpointExecutionTypeFilter)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white"
                >
                  <option value="">All execution types</option>
                  <option value="manual">Manual</option>
                  <option value="visual">Visual</option>
                  <option value="hearing">Hearing</option>
                </select>
              </div>
            )}

            {loadingAssetCheckpoints ? (
              <div className="text-center py-12 text-gray-500">
                <Loader2 size={32} className="mx-auto mb-4 text-gray-400 animate-spin" />
                <p className="text-sm">Loading checkpoints...</p>
              </div>
            ) : assetCheckpoints.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No asset checkpoints found</p>
                <p className="text-xs text-gray-400 mt-1">Click "Create Custom Checkpoint" to add checkpoints</p>
              </div>
            ) : filterCheckpointList(assetCheckpoints).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Search size={40} className="mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No checkpoints match your search/filter</p>
                <button
                  onClick={resetCheckpointFilters}
                  className="text-xs font-medium text-emerald-600 hover:underline mt-1"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    {filterCheckpointList(assetCheckpoints).length} of {assetCheckpoints.length} checkpoint{assetCheckpoints.length !== 1 ? 's' : ''} shown
                  </p>
                </div>
                <div className="space-y-3">
                  {filterCheckpointList(assetCheckpoints).map((checkpoint, index) => {
                    const rawId = (checkpoint as any)._id || (checkpoint as any).id;
                    const checkpointId = rawId ? String(rawId) : `temp-${index}`;
                    const isExpanded = expandedAssetCheckpoint === checkpointId;
                    const isEditing = editingAssetCheckpoint === checkpointId;
                    const approval = assetCheckpointApprovalMap.get(checkpointId);
                    const wasRejected = approval?.status === 'rejected';
                    const isUpdated = selectedAuditId ? updatedCheckpointIds.has(`${selectedAuditId}:${checkpointId}`) : false;
                    const showRejected = wasRejected && !isUpdated;
                    const showUpdatedTag = wasRejected && isUpdated;

                    return (
                      <div
                        key={checkpointId}
                        className={`bg-white rounded-lg border shadow-sm transition-all duration-200 ${
                          showRejected
                            ? 'border-red-300 ring-1 ring-red-100 bg-red-50/30'
                            : showUpdatedTag
                            ? 'border-amber-300 ring-1 ring-amber-100 bg-amber-50/20'
                            : isExpanded
                            ? 'border-emerald-300 bg-emerald-50/30'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {/* Header */}
                        <div
                          className={`p-4 flex items-start justify-between ${!isEditing ? 'cursor-pointer' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isEditing) {
                              handleCheckpointClick(checkpointId, 'asset');
                            }
                          }}
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div className="mt-0.5" title={showRejected ? 'Rejected — update and resubmit' : undefined}>
                              {showRejected ? (
                                <XCircle size={18} className="text-red-600 flex-shrink-0" />
                              ) : checkpoint.status === 'active' ? (
                                <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                              ) : (
                                <XCircle size={18} className="text-red-600 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h4 className="text-sm font-semibold text-gray-900 leading-tight">
                                  {checkpoint.name}
                                </h4>
                                {showRejected && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                    Rejected
                                  </span>
                                )}
                                {showUpdatedTag && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                    Updated
                                  </span>
                                )}
                              </div>
                              {!isExpanded && (
                                <div className="flex flex-wrap items-start gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium">Execution:</span>
                                    <span className="capitalize">{checkpoint.checkpointMeta?.executionType || 'N/A'}</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span>
                                      Created by{' '}
                                      <span className="font-medium text-gray-700">
                                        {typeof checkpoint.createdBy === 'object'
                                          ? checkpoint.createdBy?.name || 'N/A'
                                          : 'N/A'}
                                      </span>
                                    </span>
                                    {checkpoint.createdAt && (
                                      <span className="text-gray-400 mt-0.5">
                                        {formatDateTime(checkpoint.createdAt)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronUp size={16} className="text-gray-400" />
                            ) : (
                              <ChevronDown size={16} className="text-gray-400" />
                            )}
                            {!isEditing && (
                              <button
                                onClick={(e) => handleEditCheckpoint(checkpointId, 'asset', e)}
                                className="p-1.5 rounded-md text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                title="Edit checkpoint"
                              >
                                <Edit2 size={14} />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleToggleCheckpointStatus(checkpoint, 'asset', e)}
                              disabled={togglingCheckpointId === checkpointId}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                                checkpoint.status === 'active'
                                  ? 'bg-green-500'
                                  : 'bg-gray-300'
                              } ${togglingCheckpointId === checkpointId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              title={`${checkpoint.status === 'active' ? 'Deactivate' : 'Activate'} checkpoint`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                  checkpoint.status === 'active' ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            className="border-t border-gray-100 bg-white p-6"
                          >
                            {isEditing ? (
                              <AmsAuditCheckpointForm
                                checkpoint={checkpoint}
                                amsProjectAuditId={selectedAuditId}
                                checkpointType="asset"
                                auditTypeId={getSelectedAuditTypeId(selectedAudit)}
                                projectAssets={projectAssets.map(asset => ({
                                  _id: asset._id || asset.id || '',
                                  name: asset.name || 'Unknown Asset',
                                }))}
                                onSuccess={() => {
                                  if (wasRejected && selectedAuditId) {
                                    setUpdatedCheckpointIds(prev => new Set(prev).add(`${selectedAuditId}:${checkpointId}`));
                                    const aid = selectedAssetForAuditCheckpoints?._id ?? (selectedAssetForAuditCheckpoints as any)?.id;
                                    if (aid) setUpdatedAssetIds(prev => new Set(prev).add(`${selectedAuditId}:${String(aid)}`));
                                    onAuditCheckpointsUpdated?.(selectedAuditId);
                                  }
                                  setEditingAssetCheckpoint(null);
                                  if (selectedAssetForAuditCheckpoints) {
                                    fetchAssetCheckpoints(selectedAssetForAuditCheckpoints._id || selectedAssetForAuditCheckpoints.id || '', extractMasterAssetId(selectedAssetForAuditCheckpoints));
                                  }
                                  fetchActiveCheckpointCounts();
                                  onCheckpointsUpdated?.();
                                }}
                                onCancel={() => {
                                  setEditingAssetCheckpoint(null);
                                }}
                                mode="edit"
                              />
                            ) : (
                              <div className="space-y-6">
                                {/* Rejection reason — show so user can read and update */}
                                {showRejected && approval?.approverComments && (
                                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                                    <h5 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                                      <XCircle size={16} /> Rejection reason
                                    </h5>
                                    <p className="text-sm text-red-700 whitespace-pre-wrap">{approval.approverComments}</p>
                                    <p className="text-xs text-red-600 mt-2">Update this checkpoint and resubmit for approval.</p>
                                  </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">Checkpoint Name</label>
                                    <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                      {checkpoint.name}
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">Project Asset</label>
                                    <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                      {(() => {
                                        const assetIdObj = checkpoint.amsProjectAssetId;
                                        if (assetIdObj && typeof assetIdObj === 'object' && !Array.isArray(assetIdObj)) {
                                          return (assetIdObj as any).name || 'Unknown Asset';
                                        } else if (checkpoint.amsProjectAsset?.name) {
                                          return checkpoint.amsProjectAsset.name;
                                        } else {
                                          return selectedAssetForAuditCheckpoints?.name || 'Not assigned';
                                        }
                                      })()}
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">Execution Type</label>
                                    <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300 capitalize">
                                      {checkpoint.checkpointMeta?.executionType || 'N/A'}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.documentApplicable || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Document Applicable</label>
                                  </div>
                                  {checkpoint.checkpointMeta?.documentApplicable && checkpoint.checkpointMeta?.documentTypes && checkpoint.checkpointMeta.documentTypes.length > 0 && (
                                    <div className="col-span-2 space-y-2">
                                      <label className="block text-sm font-semibold text-gray-700">Document Types</label>
                                      <div className="flex flex-wrap gap-2">
                                        {checkpoint.checkpointMeta.documentTypes.map((docType: string, idx: number) => (
                                          <span key={idx} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-sm">
                                            {docType}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.designedParameterAvailable || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Designed Parameter Available</label>
                                  </div>
                                  {checkpoint.checkpointMeta?.designedParameterAvailable && (
                                    <>
                                      {checkpoint.checkpointMeta.designedParameterCategory && (
                                        <div className="space-y-2">
                                          <label className="block text-sm font-semibold text-gray-700">Designed Parameter Category</label>
                                          <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                            {checkpoint.checkpointMeta.designedParameterCategory}
                                          </div>
                                        </div>
                                      )}
                                      {checkpoint.checkpointMeta.designedParameterUom && (
                                        <div className="space-y-2">
                                          <label className="block text-sm font-semibold text-gray-700">Designed Parameter UOM</label>
                                          <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                            {checkpoint.checkpointMeta.designedParameterUom}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.imagesEnabled || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Images Enabled</label>
                                  </div>
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.remarksEnabled || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Remarks Enabled</label>
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checkpoint Master entries not yet part of this audit — could be flagged
                for a different audit type, or just added after this audit's checkpoints
                were generated. Toggling one on materializes it into this audit. */}
            {!loadingAvailableAssetCheckpoints && filterCheckpointList(availableAssetCheckpoints).length > 0 && (
              <div className="space-y-3 pt-4 mt-4 border-t border-dashed border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {filterCheckpointList(availableAssetCheckpoints).length} checkpoint{filterCheckpointList(availableAssetCheckpoints).length !== 1 ? 's' : ''} available to add
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Defined in Checkpoint Master for this asset but not part of this audit yet. Toggle one on to add it here — it won't duplicate a checkpoint already in this audit.
                  </p>
                </div>
                <div className="space-y-3">
                  {filterCheckpointList(availableAssetCheckpoints).map((masterCheckpoint) => {
                    const masterId = String(masterCheckpoint._id || masterCheckpoint.id);
                    const isMaterializing = materializingCheckpointId === masterId;
                    const applicableAuditTypes = getApplicableAuditTypeLabels(masterCheckpoint.checkpointMeta);
                    return (
                      <div
                        key={masterId}
                        className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-4 flex items-start justify-between gap-3"
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-700 leading-tight">{masterCheckpoint.name}</h4>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-1">
                              <span className="capitalize">Execution: {masterCheckpoint.checkpointMeta?.executionType || 'N/A'}</span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600">
                                Not in this audit
                              </span>
                            </div>
                            {applicableAuditTypes.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                <span className="text-[10px] text-gray-400">Applies to:</span>
                                {applicableAuditTypes.map((label) => (
                                  <span
                                    key={label}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-100"
                                  >
                                    {label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleMaterializeAssetCheckpoint(masterCheckpoint)}
                          disabled={isMaterializing}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 mt-0.5 ${
                            isMaterializing ? 'bg-gray-300 opacity-50 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                          title="Add this checkpoint to the current audit"
                        >
                          <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white translate-x-0.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </RightDrawer>
      )}
      {/* Project-level Checkpoints Drawer (not tied to a specific asset) */}
      {selectedAuditId && selectedAudit && (
        <RightDrawer
          open={showProjectCheckpointsDrawer}
          onClose={() => {
            setShowProjectCheckpointsDrawer(false);
            setProjectCheckpoints([]);
            setAvailableProjectCheckpoints([]);
            setExpandedAssetCheckpoint(null);
            setEditingAssetCheckpoint(null);
            setShowCreateProjectCheckpoint(false);
            resetCheckpointFilters();
          }}
          title={`Project Checkpoints - ${getSelectedAuditTypeLabel(selectedAudit)}`}
          size="3xl"
        >
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Building2 size={20} className="text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-emerald-900">Project Checkpoints</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    Checkpoints that apply to the whole project for this audit — not tied to any specific asset.
                  </p>
                </div>
              </div>
            </div>

            {/* Create Custom Checkpoint Button */}
            {!showCreateProjectCheckpoint && (
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCreateProjectCheckpoint(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  <Plus size={16} />
                  Create Custom Checkpoint
                </button>
              </div>
            )}

            {/* Create Form Card */}
            {showCreateProjectCheckpoint && selectedAudit && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Create Custom Project Checkpoint</h4>
                  <button
                    onClick={() => setShowCreateProjectCheckpoint(false)}
                    className="p-1.5 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100"
                  >
                    <X size={18} />
                  </button>
                </div>
                <AmsAuditCheckpointForm
                  amsProjectAuditId={selectedAuditId}
                  checkpointType="project"
                  auditTypeId={getSelectedAuditTypeId(selectedAudit)}
                  onSuccess={() => {
                    setShowCreateProjectCheckpoint(false);
                    fetchProjectCheckpoints();
                    fetchActiveCheckpointCounts();
                    onCheckpointsUpdated?.();
                  }}
                  onCancel={() => setShowCreateProjectCheckpoint(false)}
                  mode="create"
                />
              </div>
            )}

            {!loadingProjectCheckpoints && projectCheckpoints.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={checkpointSearchQuery}
                    onChange={(e) => setCheckpointSearchQuery(e.target.value)}
                    placeholder="Search checkpoints by name..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  />
                </div>
                <select
                  value={checkpointExecutionTypeFilter}
                  onChange={(e) => setCheckpointExecutionTypeFilter(e.target.value as typeof checkpointExecutionTypeFilter)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white"
                >
                  <option value="">All execution types</option>
                  <option value="manual">Manual</option>
                  <option value="visual">Visual</option>
                  <option value="hearing">Hearing</option>
                </select>
              </div>
            )}

            {loadingProjectCheckpoints ? (
              <div className="text-center py-12 text-gray-500">
                <Loader2 size={32} className="mx-auto mb-4 text-gray-400 animate-spin" />
                <p className="text-sm">Loading checkpoints...</p>
              </div>
            ) : projectCheckpoints.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No project-level checkpoints found</p>
                <p className="text-xs text-gray-400 mt-1">Click "Create Custom Checkpoint" to add checkpoints</p>
              </div>
            ) : filterCheckpointList(projectCheckpoints).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Search size={40} className="mx-auto mb-4 text-gray-300" />
                <p className="text-sm">No checkpoints match your search/filter</p>
                <button
                  onClick={resetCheckpointFilters}
                  className="text-xs font-medium text-emerald-600 hover:underline mt-1"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    {filterCheckpointList(projectCheckpoints).length} of {projectCheckpoints.length} checkpoint{projectCheckpoints.length !== 1 ? 's' : ''} shown
                  </p>
                </div>
                <div className="space-y-3">
                  {filterCheckpointList(projectCheckpoints).map((checkpoint, index) => {
                    const rawId = (checkpoint as any)._id || (checkpoint as any).id;
                    const checkpointId = rawId ? String(rawId) : `temp-${index}`;
                    const isExpanded = expandedAssetCheckpoint === checkpointId;
                    const isEditing = editingAssetCheckpoint === checkpointId;
                    const approval = projectCheckpointApprovalMap.get(checkpointId);
                    const wasRejected = approval?.status === 'rejected';
                    const isUpdated = selectedAuditId ? updatedCheckpointIds.has(`${selectedAuditId}:${checkpointId}`) : false;
                    const showRejected = wasRejected && !isUpdated;
                    const showUpdatedTag = wasRejected && isUpdated;

                    return (
                      <div
                        key={checkpointId}
                        className={`bg-white rounded-lg border shadow-sm transition-all duration-200 ${
                          showRejected
                            ? 'border-red-300 ring-1 ring-red-100 bg-red-50/30'
                            : showUpdatedTag
                            ? 'border-amber-300 ring-1 ring-amber-100 bg-amber-50/20'
                            : isExpanded
                            ? 'border-emerald-300 bg-emerald-50/30'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {/* Header */}
                        <div
                          className={`p-4 flex items-start justify-between ${!isEditing ? 'cursor-pointer' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isEditing) {
                              handleCheckpointClick(checkpointId, 'project');
                            }
                          }}
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div className="mt-0.5" title={showRejected ? 'Rejected — update and resubmit' : undefined}>
                              {showRejected ? (
                                <XCircle size={18} className="text-red-600 flex-shrink-0" />
                              ) : checkpoint.status === 'active' ? (
                                <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                              ) : (
                                <XCircle size={18} className="text-red-600 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h4 className="text-sm font-semibold text-gray-900 leading-tight">
                                  {checkpoint.name}
                                </h4>
                                {showRejected && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                    Rejected
                                  </span>
                                )}
                                {showUpdatedTag && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                    Updated
                                  </span>
                                )}
                              </div>
                              {!isExpanded && (
                                <div className="flex flex-wrap items-start gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium">Execution:</span>
                                    <span className="capitalize">{checkpoint.checkpointMeta?.executionType || 'N/A'}</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span>
                                      Created by{' '}
                                      <span className="font-medium text-gray-700">
                                        {typeof checkpoint.createdBy === 'object'
                                          ? checkpoint.createdBy?.name || 'N/A'
                                          : 'N/A'}
                                      </span>
                                    </span>
                                    {checkpoint.createdAt && (
                                      <span className="text-gray-400 mt-0.5">
                                        {formatDateTime(checkpoint.createdAt)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronUp size={16} className="text-gray-400" />
                            ) : (
                              <ChevronDown size={16} className="text-gray-400" />
                            )}
                            {!isEditing && (
                              <button
                                onClick={(e) => handleEditCheckpoint(checkpointId, 'project', e)}
                                className="p-1.5 rounded-md text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                title="Edit checkpoint"
                              >
                                <Edit2 size={14} />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleToggleCheckpointStatus(checkpoint, 'project', e)}
                              disabled={togglingCheckpointId === checkpointId}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                                checkpoint.status === 'active'
                                  ? 'bg-green-500'
                                  : 'bg-gray-300'
                              } ${togglingCheckpointId === checkpointId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              title={`${checkpoint.status === 'active' ? 'Deactivate' : 'Activate'} checkpoint`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                  checkpoint.status === 'active' ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            className="border-t border-gray-100 bg-white p-6"
                          >
                            {isEditing ? (
                              <AmsAuditCheckpointForm
                                checkpoint={checkpoint}
                                amsProjectAuditId={selectedAuditId}
                                checkpointType="project"
                                auditTypeId={getSelectedAuditTypeId(selectedAudit)}
                                onSuccess={() => {
                                  if (wasRejected && selectedAuditId) {
                                    setUpdatedCheckpointIds(prev => new Set(prev).add(`${selectedAuditId}:${checkpointId}`));
                                    onAuditCheckpointsUpdated?.(selectedAuditId);
                                  }
                                  setEditingAssetCheckpoint(null);
                                  fetchProjectCheckpoints();
                                  fetchActiveCheckpointCounts();
                                  onCheckpointsUpdated?.();
                                }}
                                onCancel={() => {
                                  setEditingAssetCheckpoint(null);
                                }}
                                mode="edit"
                              />
                            ) : (
                              <div className="space-y-6">
                                {/* Rejection reason — show so user can read and update */}
                                {showRejected && approval?.approverComments && (
                                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                                    <h5 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                                      <XCircle size={16} /> Rejection reason
                                    </h5>
                                    <p className="text-sm text-red-700 whitespace-pre-wrap">{approval.approverComments}</p>
                                    <p className="text-xs text-red-600 mt-2">Update this checkpoint and resubmit for approval.</p>
                                  </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">Checkpoint Name</label>
                                    <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                      {checkpoint.name}
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">Execution Type</label>
                                    <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300 capitalize">
                                      {checkpoint.checkpointMeta?.executionType || 'N/A'}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.documentApplicable || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Document Applicable</label>
                                  </div>
                                  {checkpoint.checkpointMeta?.documentApplicable && checkpoint.checkpointMeta?.documentTypes && checkpoint.checkpointMeta.documentTypes.length > 0 && (
                                    <div className="col-span-2 space-y-2">
                                      <label className="block text-sm font-semibold text-gray-700">Document Types</label>
                                      <div className="flex flex-wrap gap-2">
                                        {checkpoint.checkpointMeta.documentTypes.map((docType: string, idx: number) => (
                                          <span key={idx} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-sm">
                                            {docType}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.designedParameterAvailable || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Designed Parameter Available</label>
                                  </div>
                                  {checkpoint.checkpointMeta?.designedParameterAvailable && (
                                    <>
                                      {checkpoint.checkpointMeta.designedParameterCategory && (
                                        <div className="space-y-2">
                                          <label className="block text-sm font-semibold text-gray-700">Designed Parameter Category</label>
                                          <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                            {checkpoint.checkpointMeta.designedParameterCategory}
                                          </div>
                                        </div>
                                      )}
                                      {checkpoint.checkpointMeta.designedParameterUom && (
                                        <div className="space-y-2">
                                          <label className="block text-sm font-semibold text-gray-700">Designed Parameter UOM</label>
                                          <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                                            {checkpoint.checkpointMeta.designedParameterUom}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.imagesEnabled || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Images Enabled</label>
                                  </div>
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checkpoint.checkpointMeta?.remarksEnabled || false}
                                      disabled
                                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <label className="ml-2 block text-sm font-semibold text-gray-700">Remarks Enabled</label>
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checkpoint Master entries not yet part of this audit — could be flagged
                for a different audit type, or just added after this audit's checkpoints
                were generated. Toggling one on materializes it into this audit. */}
            {!loadingAvailableProjectCheckpoints && filterCheckpointList(availableProjectCheckpoints).length > 0 && (
              <div className="space-y-3 pt-4 mt-4 border-t border-dashed border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {filterCheckpointList(availableProjectCheckpoints).length} checkpoint{filterCheckpointList(availableProjectCheckpoints).length !== 1 ? 's' : ''} available to add
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Defined in Checkpoint Master for this project type but not part of this audit yet. Toggle one on to add it here — it won't duplicate a checkpoint already in this audit.
                  </p>
                </div>
                <div className="space-y-3">
                  {filterCheckpointList(availableProjectCheckpoints).map((masterCheckpoint) => {
                    const masterId = String(masterCheckpoint._id || masterCheckpoint.id);
                    const isMaterializing = materializingCheckpointId === masterId;
                    const applicableAuditTypes = getApplicableAuditTypeLabels(masterCheckpoint.checkpointMeta);
                    return (
                      <div
                        key={masterId}
                        className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-4 flex items-start justify-between gap-3"
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-700 leading-tight">{masterCheckpoint.name}</h4>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-1">
                              <span className="capitalize">Execution: {masterCheckpoint.checkpointMeta?.executionType || 'N/A'}</span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600">
                                Not in this audit
                              </span>
                            </div>
                            {applicableAuditTypes.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                <span className="text-[10px] text-gray-400">Applies to:</span>
                                {applicableAuditTypes.map((label) => (
                                  <span
                                    key={label}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-100"
                                  >
                                    {label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleMaterializeProjectCheckpoint(masterCheckpoint)}
                          disabled={isMaterializing}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 mt-0.5 ${
                            isMaterializing ? 'bg-gray-300 opacity-50 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                          title="Add this checkpoint to the current audit"
                        >
                          <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white translate-x-0.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </RightDrawer>
      )}
      </div>
    </>
  );
}
